// Dashboard.js - VERSÃO CORRIGIDA
// 🔥 Countdown de 30s NÃO REINICIA com novas tentativas
// 🔥 Recuperação automática: se o servidor voltar, faz refresh e volta ao estado anterior
// 🔥 Página ativa guardada em localStorage e restaurada ao recarregar
// 🔥 SUBMENU PERSISTENTE: apenas UM grupo aberto de cada vez, guardado em localStorage
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import './Dashboard.css';
import Analitico from './Analitico';
import DashMenu from './DashMenu';
import Empresas from './Empresas';
import Utilizadores from './Utilizadores';
import Logs from './Logs';
import Licencas from './Licencas';
import Definicoes from './Definicoes';
import Noticias from './Noticias';
import Clientes from './Clientes';
import Fornecedores from './Fornecedores';
import Produtos from './CriarProdutos';
import ListarProdutos from './ListarProdutos';
import EntradaStock from './EntradaStock';
import Categorias from './Categorias';
import Movimentos from './Movimentos';
import Inventario from './Inventario';
import Armazens from './Armazens';
import Cotacoes from './Cotacoes';
import Servicos from './Servicos';
import Faturas from './Faturas';
import Recibos from './Recibos';
import DashboardVendas from './DashboardVendas';
import Financeiro from './Financeiro';
import GuiasEntrega from './GuiasEntrega';
import Recebimentos from './Recebimentos';
import RelatorioVendas from './RelatorioVendas';
import Relatorios from './Relatorios';

import { Avatar, ProgressCircle, Icon } from './SharedComponents';
import {
    menuStructure as menuStructureOriginal,
    filtrarMenuPorModulos,
    filtrarMenuCompleto,
    getMenuEnriquecido,
    PERMISSOES_POR_PERFIL
} from '../utils/menuStructure';

// ==============================================
// 🔥 INTERCEPTOR AXIOS GLOBAL — 401 → NOTIFICAR DASHBOARD
// ==============================================
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.warn('🚨 [AXIOS INTERCEPTOR] 401 detetado em:', error.config?.url);
            window.dispatchEvent(new CustomEvent('sessaoInvalida', {
                detail: { url: error.config?.url, status: 401 }
            }));
        }
        return Promise.reject(error);
    }
);

// ==============================================
// 🔥 CORES ESCURAS PRÉ-DEFINIDAS PARA O HEADER
// ==============================================
const CORES_ESCURAS_HEADER = [
    '#6a1b9a',
    '#1a1a2e',
    '#1a237e',
    '#c62828',
    '#ad1457',
    '#37474f',
    '#000000',
    '#4a342e'
];

// 🔥 Chave de armazenamento do grupo aberto
const STORAGE_KEY_GRUPO_ABERTO = 'dashboard_grupo_aberto';

/* ══════════════════════════════════════════════
   DASHBOARD PRINCIPAL
══════════════════════════════════════════════ */
export default function Dashboard() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // ✅ activeLink restaurado do localStorage (volta ao mesmo sítio após refresh)
    const [activeLink, setActiveLink] = useState(() => {
        const saved = localStorage.getItem('dashboardActiveLink');
        return saved || 'Dashboard';
    });

    const [sidebarOpen, setSidebarOpen] = useState(false);

    // ==============================================
    // 🔥 DARK MODE
    // ==============================================
    const [darkMode, setDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkModeAtivo');
        if (saved !== null) {
            return saved === 'true';
        }
        const horas = new Date().getHours();
        return horas >= 18 || horas < 6;
    });
    const [modoAutomatico, setModoAutomatico] = useState(true);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // ==============================================
    // 🔥 ESTADO PARA COR DO HEADER
    // ==============================================
    const [headerColor, setHeaderColor] = useState('#1a1a2e');
    const [carregandoCor, setCarregandoCor] = useState(false);
    const [headerColorBase, setHeaderColorBase] = useState('#1a1a2e');

    // ==============================================
    // 🔥 ESTADOS PARA MONITORAMENTO DO SERVIDOR
    // ==============================================
    const [serverStatus, setServerStatus] = useState('online');
    const [showServerError, setShowServerError] = useState(false);
    const [serverErrorCode, setServerErrorCode] = useState('');
    const [serverErrorMessage, setServerErrorMessage] = useState('');
    const [serverErrorDetail, setServerErrorDetail] = useState('');
    const [serverErrorTimestamp, setServerErrorTimestamp] = useState(null);
    const [serverErrorCount, setServerErrorCount] = useState(0);
    const [serverErrorHistory, setServerErrorHistory] = useState([]);
    const [logoutCountdown, setLogoutCountdown] = useState(30);

    // ==============================================
    // 🔥 REFS DE CONTROLO
    // ==============================================
    const serverCheckInterval = useRef(null);
    const isForceLoggingOut = useRef(false);
    const logoutTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const isOfflineHandledRef = useRef(false);

    // ✅ NOVOS REFS — controle do countdown e recuperação
    const countdownStartedRef = useRef(false);
    const wasOfflineRef = useRef(false);
    const recoveryCheckRef = useRef(null);

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
    const [confirmPopup, setConfirmPopup] = useState(null);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved === 'true';
    });

    const [rightContentCollapsed, setRightContentCollapsed] = useState(() => {
        const saved = localStorage.getItem('rightContentCollapsed');
        return saved === 'true';
    });

    // ==============================================
    // 🔥 SUBMENU ATIVO — apenas UM grupo aberto de cada vez
    // 🔥 Persistido em localStorage (compatível com versão antiga em array)
    // ==============================================
    const [expandedMenu, setExpandedMenu] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_GRUPO_ABERTO);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Compatibilidade: se for um array (versão antiga), pega só o primeiro
                if (Array.isArray(parsed)) return parsed[0] || null;
                if (typeof parsed === 'string') return parsed;
                if (parsed && typeof parsed === 'object' && parsed.id) return parsed.id;
            }
            // Tentar também a chave antiga
            const savedAntigo = localStorage.getItem('dashboard_grupos_abertos');
            if (savedAntigo) {
                const parsedAntigo = JSON.parse(savedAntigo);
                if (Array.isArray(parsedAntigo)) return parsedAntigo[0] || null;
                if (typeof parsedAntigo === 'string') return parsedAntigo;
            }
        } catch (e) {
            console.error('❌ Erro ao ler grupo aberto do localStorage:', e);
        }
        return null;
    });

    const [menuFiltrado, setMenuFiltrado] = useState([]);
    const [modulosAtivos, setModulosAtivos] = useState({});
    const [empresaInfo, setEmpresaInfo] = useState(null);
    const [permissoesIndividuais, setPermissoesIndividuais] = useState(null);

    const [reminders, setReminders] = useState([]);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [persistentNotifications, setPersistentNotifications] = useState([]);
    const [tempToast, setTempToast] = useState(null);
    const [removingNotificationId, setRemovingNotificationId] = useState(null);
    const [toastCountdown, setToastCountdown] = useState(0);
    const toastCountdownInterval = useRef(null);

    const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(() => {
        const saved = localStorage.getItem('pushNotificationsEnabled');
        return saved === 'true' ? true : false;
    });

    const reminderTimeoutsRef = useRef(new Map());
    const triggeredRemindersRef = useRef(new Set());
    const isProcessingRef = useRef(false);
    const alarmPlayingRef = useRef(false);
    const pushNotificationPermissionRef = useRef(false);

    const reminderIcons = [
        { name: 'Alarme', icon: 'alarm' },
        { name: 'Sino Ativo', icon: 'notifications_active' },
        { name: 'Check', icon: 'check_circle' },
        { name: 'Tarefa', icon: 'task_alt' },
        { name: 'Nota', icon: 'note' },
        { name: 'Evento', icon: 'event' },
    ];

    const [newReminder, setNewReminder] = useState({
        title: '',
        icon: 'alarm',
        reminderType: 'interval',
        intervalHours: 0,
        intervalMinutes: 1,
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        lastTriggered: null
    });

    // ==============================================
    // FUNÇÃO PARA OBTER URL DA IMAGEM
    // ==============================================
    const getImagemUrl = (imagemPerfil) => {
        if (!imagemPerfil) return null;
        if (imagemPerfil.startsWith('http://') || imagemPerfil.startsWith('https://')) {
            return imagemPerfil;
        }
        let cleanPath = imagemPerfil.replace(/^\/+/, '');
        if (cleanPath.startsWith('uploads/')) {
            return `/${cleanPath}`;
        }
        if (!cleanPath.startsWith('uploads/')) {
            cleanPath = `uploads/${cleanPath}`;
        }
        return `/${cleanPath}`;
    };

    // ==============================================
    // FUNÇÃO PARA EXTRAIR O ID DA EMPRESA
    // ==============================================
    const extrairEmpresaId = (empresaId) => {
        if (!empresaId) return null;
        if (typeof empresaId === 'object' && empresaId._id) {
            return empresaId._id;
        }
        if (typeof empresaId === 'object' && empresaId.id) {
            return empresaId.id;
        }
        if (typeof empresaId === 'string') {
            return empresaId;
        }
        if (typeof empresaId === 'object' && empresaId.toString) {
            return empresaId.toString();
        }
        return null;
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA ESCURECER UMA COR (DARK MODE)
    // ==============================================
    const escurecerCor = (cor, fator = 0.7) => {
        if (!cor) return '#1a1a2e';
        let hex = cor.replace('#', '');
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const novoR = Math.floor(r * fator);
        const novoG = Math.floor(g * fator);
        const novoB = Math.floor(b * fator);
        const finalR = Math.max(10, Math.min(255, novoR));
        const finalG = Math.max(10, Math.min(255, novoG));
        const finalB = Math.max(10, Math.min(255, novoB));
        return `#${finalR.toString(16).padStart(2, '0')}${finalG.toString(16).padStart(2, '0')}${finalB.toString(16).padStart(2, '0')}`;
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA CALCULAR BRILHO DA COR
    // ==============================================
    const calcularBrilho = (cor) => {
        if (!cor) return 0;
        let hex = cor.replace('#', '');
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (r * 299 + g * 587 + b * 114) / 1000;
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA DETETAR SE A COR É BRANCA
    // ==============================================
    const isCorBranca = (cor) => {
        if (!cor) return false;
        const c = cor.toString().toLowerCase().trim();
        const coresBrancas = [
            '#fff',
            '#ffffff',
            '#ffff',
            'white',
            'rgb(255, 255, 255)',
            'rgb(255,255,255)',
            'rgba(255, 255, 255, 1)',
            'rgba(255,255,255,1)',
            'rgba(255, 255, 255, 100%)',
            'hsl(0, 0%, 100%)'
        ];
        return coresBrancas.includes(c);
    };

    // ==============================================
    // 🔥 NORMALIZAR COR PARA HEX
    // ==============================================
    const normalizarParaHex = (cor) => {
        if (!cor) return null;
        const str = cor.toString().trim().toLowerCase();

        const rgbMatch = str.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1], 10);
            const g = parseInt(rgbMatch[2], 10);
            const b = parseInt(rgbMatch[3], 10);
            const toHex = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        }

        let hex = str.replace('#', '').trim();
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        if (hex.length === 6 && /^[0-9a-f]{6}$/.test(hex)) {
            return `#${hex}`;
        }
        return null;
    };

    // ==============================================
    // 🔥 DETETAR SE A COR ESTÁ NA LISTA ESCURA
    // ==============================================
    const isCorEscuraPredefinida = (cor) => {
        if (!cor) return false;
        const hexNormalizado = normalizarParaHex(cor);
        if (!hexNormalizado) return false;

        if (CORES_ESCURAS_HEADER.includes(hexNormalizado)) {
            return true;
        }

        const hexParaRgb = (h) => {
            const s = h.replace('#', '');
            return {
                r: parseInt(s.substring(0, 2), 16),
                g: parseInt(s.substring(2, 4), 16),
                b: parseInt(s.substring(4, 6), 16)
            };
        };
        const alvo = hexParaRgb(hexNormalizado);
        const tolerancia = 3;

        return CORES_ESCURAS_HEADER.some((corLista) => {
            const ref = hexParaRgb(corLista);
            return (
                Math.abs(ref.r - alvo.r) <= tolerancia &&
                Math.abs(ref.g - alvo.g) <= tolerancia &&
                Math.abs(ref.b - alvo.b) <= tolerancia
            );
        });
    };

    // ==============================================
    // 🔥 APLICAR COR AO HEADER
    // ==============================================
    const aplicarCorHeader = (cor, isDark = darkMode) => {
        const corBase = cor || '#1a1a2e';
        setHeaderColorBase(corBase);

        let corFinal = corBase;
        if (isDark) {
            const fator = isCorBranca(corBase) ? 0.25 : 0.4;
            corFinal = escurecerCor(corBase, fator);
        }

        setHeaderColor(corFinal);

        const headerElement = document.querySelector('.header');
        if (headerElement) {
            headerElement.classList.remove('header-cor-clara');
            headerElement.classList.remove('header-cor-escura');
            headerElement.classList.remove('header-cor-personalizada');

            const corBaseLower = corBase.toLowerCase().trim();
            const coresPadrao = ['#1a1a2e', 'rgb(26, 26, 46)', ''];
            const isBranca = isCorBranca(corBase);
            const isEscuraPredefinida = isCorEscuraPredefinida(corBase);

            if (isEscuraPredefinida) {
                headerElement.classList.add('header-cor-escura');
            } else if (isDark) {
                headerElement.classList.add('header-cor-escura');
            } else {
                const brilho = calcularBrilho(corBase);
                if (brilho >= 200 || isBranca) {
                    headerElement.classList.add('header-cor-clara');
                } else {
                    const isCorPersonalizada = !coresPadrao.includes(corBaseLower) && !isBranca;
                    if (isCorPersonalizada) {
                        headerElement.classList.add('header-cor-personalizada');
                    }
                }
            }
        }
    };

    // ==============================================
    // 🔥 CARREGAR COR DO HEADER DA EMPRESA
    // ==============================================
    const carregarCorHeader = async (empresaId) => {
        if (!empresaId) {
            aplicarCorHeader('#1a1a2e', darkMode);
            return;
        }

        try {
            setCarregandoCor(true);
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get(
                `/api/empresas/${empresaId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 8000
                }
            );

            if (response.data && response.data.success) {
                const empresa = response.data.data;
                const corPrimaria = empresa.configuracoes?.tema?.corPrimaria || '#1a1a2e';
                aplicarCorHeader(corPrimaria, darkMode);
            } else {
                aplicarCorHeader('#1a1a2e', darkMode);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar cor da empresa:', err);
            aplicarCorHeader('#1a1a2e', darkMode);
        } finally {
            setCarregandoCor(false);
        }
    };

    // ==============================================
    // 🔥 EXTRAIR LICENÇA
    // ==============================================
    const extrairLicenca = (dados) => {
        if (!dados) {
            return {
                ativa: false,
                tipoPrazo: 'Desativada',
                chave: 'N/A',
                dataInicio: null,
                dataFim: null,
                plano: 'N/A',
                limiteUtilizadores: 0,
                diasRestantes: null
            };
        }
        if (dados.tipoPrazo || dados.chave || dados.ativa !== undefined) {
            let diasRestantes = null;
            if (dados.tipoPrazo !== 'Infinito' && dados.tipoPrazo !== 'Desativada' && dados.dataFim) {
                const diffTime = new Date(dados.dataFim) - new Date();
                diasRestantes = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diasRestantes < 0) diasRestantes = 0;
            }
            return { ...dados, diasRestantes };
        }
        if (dados.licenca) {
            return extrairLicenca(dados.licenca);
        }
        if (dados.licencaId && typeof dados.licencaId === 'object') {
            return extrairLicenca(dados.licencaId);
        }
        if (dados.empresaId && typeof dados.empresaId === 'object' && dados.empresaId.licenca) {
            return extrairLicenca(dados.empresaId.licenca);
        }
        return {
            ativa: false,
            tipoPrazo: 'Desativada',
            chave: 'N/A',
            dataInicio: null,
            dataFim: null,
            plano: 'N/A',
            limiteUtilizadores: 0,
            diasRestantes: null
        };
    };

    // ==============================================
    // 🔥 BUSCAR LICENÇA DA API
    // ==============================================
    const buscarLicencaDaAPI = async (empresaId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token || !empresaId) return null;
            const response = await axios.get(
                `/api/licencas/empresa/${empresaId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 8000
                }
            );
            if (response.data && response.data.success) {
                return response.data.data || response.data.licenca;
            }
            if (response.data && response.data.licenca) {
                return response.data.licenca;
            }
            return null;
        } catch (err) {
            console.error('❌ Erro ao buscar licença da API:', err.message);
            return null;
        }
    };

    // ==============================================
    // 🔥 VERIFICAR SE O MÓDULO NOTÍCIAS ESTÁ ATIVO
    // ==============================================
    const noticiasEstaAtivo = () => {
        if (user?.perfil === 'Superadmin') {
            if (user?.noticiasConfig) {
                return user.noticiasConfig.ativo === true;
            }
            return true;
        }

        if (user && user.noticiasConfig !== undefined && user.noticiasConfig !== null) {
            const config = user.noticiasConfig;

            if (typeof config === 'object' && config !== null) {
                if (config.ativo === true || config.ativo === 'true') return true;
                if (config.ativo === false || config.ativo === 'false') return false;
                if (config.habilitado === true || config.habilitado === 'true') return true;
                if (config.enabled === true || config.enabled === 'true') return true;
                if (modulosAtivos && modulosAtivos.noticias) {
                    return modulosAtivos.noticias.ativo === true;
                }
                return true;
            }

            if (typeof config === 'string') {
                const valorLower = config.toLowerCase();
                return valorLower === 'ativo' || valorLower === 'active' || valorLower === 'true';
            }

            if (typeof config === 'boolean') {
                return config === true;
            }
        }

        if (modulosAtivos && modulosAtivos.noticias) {
            return modulosAtivos.noticias.ativo === true;
        }

        return false;
    };

    // ==============================================
    // 🔥 FILTRAR MENU
    // ==============================================
    const filtrarMenu = (modulos, perfil, empresaNome = 'Empresa', permissoesIndividuaisParam = null) => {
        if (!modulos || Object.keys(modulos).length === 0) {
            return getMenuEnriquecido();
        }
        if (perfil === 'Superadmin') {
            return getMenuEnriquecido();
        }
        const empresaParaFiltro = {
            _id: empresaInfo?._id || 'empresa-id',
            nome: empresaNome,
            perfil: perfil || 'Cliente',
            configuracoes: { modulosAtivos: modulos }
        };
        const filtrado = filtrarMenuCompleto(empresaParaFiltro, perfil, permissoesIndividuaisParam, getMenuEnriquecido());
        const filtradoSemNoticias = filtrado.filter(item => item.id !== 'noticias');
        return filtradoSemNoticias;
    };

    // ==============================================
    // 🔥 CARREGAR MÓDULOS DA EMPRESA
    // ==============================================
    const carregarModulosDaEmpresa = async (empresaId, perfilParam = null) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('❌ Token não encontrado');
                return null;
            }
            const empresaIdStr = extrairEmpresaId(empresaId);
            if (!empresaIdStr) {
                console.error('❌ ID da empresa inválido:', empresaId);
                setMenuFiltrado(getMenuEnriquecido());
                return getMenuEnriquecido();
            }

            const perfil = perfilParam || user?.perfil || 'Cliente';

            const response = await axios.get(
                `/api/empresas/${empresaIdStr}/modulos`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.data.success) {
                const modulos = response.data.data;
                const empresa = response.data.empresa || { _id: empresaIdStr, nome: 'Empresa' };
                setModulosAtivos(modulos);
                setEmpresaInfo(empresa);
                const filtrado = filtrarMenu(modulos, perfil, empresa.nome, permissoesIndividuais);
                setMenuFiltrado(filtrado);
                return filtrado;
            } else {
                setMenuFiltrado(getMenuEnriquecido());
                return getMenuEnriquecido();
            }
        } catch (err) {
            console.error('❌ Erro ao carregar módulos:', err.message);
            const modulosFallback = {
                dashboard: { ativo: true, submodulos: { overview: true, estatisticas: true, atividades_recentes: true, graficos: true } },
                vendas: { ativo: true, submodulos: { faturas: true, cotacoes: true, recibos: true, comissoes: false } },
                comercial: { ativo: true, submodulos: { clientes: true, fornecedores: true } },
                stock: { ativo: true, submodulos: { produtos: true, criar_produto: true, servicos: true, entrada_stock: true, categorias: true, movimentos: true, inventario: true, armazens: true } },
                analise: { ativo: true, submodulos: { analitico: true, relatorios: true, financeiro: true } },
                admin: { ativo: false, submodulos: { utilizadores: true, empresas: false, licencas: false, logs: false, definicoes: true } }
            };
            setModulosAtivos(modulosFallback);
            const perfil = perfilParam || user?.perfil || 'Cliente';
            const filtrado = filtrarMenu(modulosFallback, perfil, 'Empresa', permissoesIndividuais);
            setMenuFiltrado(filtrado);
            return getMenuEnriquecido();
        }
    };

    // ==============================================
    // 🔥 VERIFICAR ACESSO A MÓDULOS
    // ==============================================
    const temAcessoModulo = (modulo, submodulo = null) => {
        if (user?.perfil === 'Superadmin') return true;
        if (!modulosAtivos || Object.keys(modulosAtivos).length === 0) return true;
        if (permissoesIndividuais && permissoesIndividuais[modulo] !== undefined) {
            if (submodulo) {
                return permissoesIndividuais[modulo].submodulos?.[submodulo] === true;
            }
            return permissoesIndividuais[modulo].ativo === true;
        }
        const moduloAtivo = modulosAtivos[modulo]?.ativo === true;
        if (!moduloAtivo) return false;
        if (submodulo) {
            return modulosAtivos[modulo]?.submodulos?.[submodulo] === true;
        }
        return true;
    };

    // ==============================================
    // 🔥 FUNÇÃO canView
    // ==============================================
    const canView = (allowedRoles, modulo = null, submodulo = null) => {
        if (!user) return false;
        if (user.perfil === 'Superadmin') return true;
        if (permissoesIndividuais && modulo) {
            if (submodulo) {
                const temPermissao = permissoesIndividuais[modulo]?.submodulos?.[submodulo] === true;
                if (temPermissao) return true;
                return allowedRoles ? allowedRoles.includes(user.perfil) : false;
            }
            const temPermissao = permissoesIndividuais[modulo]?.ativo === true;
            if (temPermissao) return true;
        }
        return allowedRoles ? allowedRoles.includes(user.perfil) : false;
    };

    // ==============================================
    // 🔥 TOGGLE DE MENU — apenas UM grupo aberto
    // Se clicar no grupo já aberto → fecha
    // Se clicar noutro grupo → fecha o anterior e abre este
    // ==============================================
    const toggleMenu = (menuId) => {
        setExpandedMenu(prev => {
            if (prev === menuId) {
                return null; // clicou no mesmo → fecha
            }
            return menuId; // clicou noutro → troca (fecha o anterior implicitamente)
        });
    };

    // ==============================================
    // 🔥 VERIFICAR SE MENU ESTÁ EXPANDIDO
    // ==============================================
    const isMenuExpanded = (menuId) => {
        return expandedMenu === menuId;
    };

    const closeAllMenus = () => {
        setExpandedMenu(null);
    };

    const handleMenuItemClick = (link, menuId = null) => {
        setActiveLink(link);
        setSidebarOpen(false);
        if (link === 'Dashboard') {
            closeAllMenus();
        }
    };

    // ==============================================
    // 🔥 GUARDAR PÁGINA ATIVA (para restaurar após recuperação)
    // ==============================================
    useEffect(() => {
        if (activeLink) {
            localStorage.setItem('dashboardActiveLink', activeLink);
        }
    }, [activeLink]);

    // ==============================================
    // 🔥 GUARDAR GRUPO ABERTO NO localStorage
    // ==============================================
    useEffect(() => {
        try {
            if (expandedMenu) {
                localStorage.setItem(STORAGE_KEY_GRUPO_ABERTO, JSON.stringify(expandedMenu));
            } else {
                localStorage.removeItem(STORAGE_KEY_GRUPO_ABERTO);
            }
            // Limpar chave antiga (array) para não confundir
            localStorage.removeItem('dashboard_grupos_abertos');
        } catch (e) {
            console.error('❌ Erro ao guardar grupo aberto:', e);
        }
    }, [expandedMenu]);

    // ==============================================
    // 🔥 ABRIR AUTOMATICAMENTE O GRUPO DO LINK ATIVO
    // Se o link ativo pertence a um grupo, esse grupo fica aberto
    // (fechando qualquer outro)
    // ==============================================
    useEffect(() => {
        if (!activeLink || menuFiltrado.length === 0) return;

        // Procurar em qual grupo está o activeLink
        const grupoDoAtivo = menuFiltrado.find(grupo =>
            grupo.children?.some(child => child.link === activeLink)
        );

        if (grupoDoAtivo) {
            setExpandedMenu(prev => {
                // Já é este grupo → não faz nada
                if (prev === grupoDoAtivo.id) return prev;
                // Troca para este grupo (fecha o anterior implicitamente)
                return grupoDoAtivo.id;
            });
        }
    }, [activeLink, menuFiltrado]);

    // ==============================================
    // 🔥 SINCRONIZAÇÃO DE DARK MODE COM A HORA DO DIA
    // ==============================================
    useEffect(() => {
        const aplicarModoPorHora = () => {
            const horas = new Date().getHours();
            const noite = horas >= 18 || horas < 6;
            setDarkMode(noite);
        };

        if (modoAutomatico) {
            aplicarModoPorHora();
        }

        const intervalId = setInterval(() => {
            if (modoAutomatico) {
                aplicarModoPorHora();
            }
        }, 60000);

        const handleFocus = () => {
            if (modoAutomatico) {
                aplicarModoPorHora();
            }
        };
        window.addEventListener('focus', handleFocus);

        const handleModoDoLogin = (event) => {
            const { darkMode: modoEscuro } = event.detail || {};
            if (typeof modoEscuro === 'boolean') {
                setDarkMode(modoEscuro);
                setModoAutomatico(false);
            }
        };
        window.addEventListener('modoDarkModeLogin', handleModoDoLogin);

        const handleStorageChange = (e) => {
            if (e.key === 'darkModeAtivo') {
                const novoModo = e.newValue === 'true';
                setDarkMode(novoModo);
                setModoAutomatico(false);
            }
        };
        window.addEventListener('storage', handleStorageChange);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('modoDarkModeLogin', handleModoDoLogin);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [modoAutomatico]);

    // ==============================================
    // 🔥 GUARDAR ESTADO DO DARK MODE
    // ==============================================
    useEffect(() => {
        localStorage.setItem('darkModeAtivo', darkMode ? 'true' : 'false');
    }, [darkMode]);

    // ==============================================
    // VERIFICAÇÃO DE EXPIRAÇÃO DO TOKEN
    // ==============================================
    useEffect(() => {
        const checkTokenExpiration = () => {
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const exp = payload.exp * 1000;
                const now = Date.now();
                const timeLeft = exp - now;
                if (timeLeft <= 0) {
                    setShowSessionExpiredModal(true);
                    return;
                }
                const warningTime = 30 * 1000;
                if (timeLeft > warningTime) {
                    const timeoutId = setTimeout(() => {
                        setShowSessionExpiredModal(true);
                    }, timeLeft - warningTime);
                    return () => clearTimeout(timeoutId);
                } else if (timeLeft <= warningTime && timeLeft > 0) {
                    setShowSessionExpiredModal(true);
                }
            } catch (error) {
                console.error('Erro ao decodificar token:', error);
            }
        };
        checkTokenExpiration();
        const intervalId = setInterval(checkTokenExpiration, 60000);
        return () => clearInterval(intervalId);
    }, []);

    // ==============================================
    // FUNÇÕES PARA SESSÃO EXPIRADA
    // ==============================================
    const handleSessionExpired = () => {
        setShowSessionExpiredModal(false);
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        localStorage.removeItem('licenca');
        localStorage.removeItem('dashboardActiveLink');
        window.location.href = '/';
    };

    const handleRelogin = () => {
        setShowSessionExpiredModal(false);
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        localStorage.removeItem('licenca');
        localStorage.removeItem('dashboardActiveLink');
        window.location.href = '/';
    };

    // ==============================================
    // 🔥 DETEÇÃO INSTANTÂNEA DE LOGOUT / TOKEN REMOVIDO
    // ==============================================
    useEffect(() => {
        const verificarSessao = () => {
            if (isForceLoggingOut.current) return;

            const token = localStorage.getItem('token');
            const usuario = localStorage.getItem('usuario');

            const semToken = !token || token === '' || token === 'null' || token === 'undefined';
            const semUsuario = !usuario || usuario === '' || usuario === 'null' || usuario === 'undefined';

            if (semToken || semUsuario) {
                setUser(null);
                setLoading(false);
                if (serverCheckInterval.current) {
                    clearInterval(serverCheckInterval.current);
                    serverCheckInterval.current = null;
                }
            }
        };

        const handleStorageEvent = (e) => {
            if (e.key === 'token' || e.key === 'usuario' || e.key === null) {
                verificarSessao();
            }
        };
        window.addEventListener('storage', handleStorageEvent);

        const handleSessaoInvalida = (event) => {
            if (isForceLoggingOut.current) return;
            localStorage.removeItem('token');
            localStorage.removeItem('usuario');
            localStorage.removeItem('licenca');
            setUser(null);
            setLoading(false);
            if (serverCheckInterval.current) {
                clearInterval(serverCheckInterval.current);
                serverCheckInterval.current = null;
            }
        };
        window.addEventListener('sessaoInvalida', handleSessaoInvalida);

        const pollInterval = setInterval(verificarSessao, 500);
        verificarSessao();

        return () => {
            window.removeEventListener('storage', handleStorageEvent);
            window.removeEventListener('sessaoInvalida', handleSessaoInvalida);
            clearInterval(pollInterval);
        };
    }, []);

    // ==============================================
    // 🔥 TOOLTIPS DINÂMICOS
    // ==============================================
    useEffect(() => {
        if (sidebarCollapsed) {
            const showTooltip = (e) => {
                const target = e.currentTarget;
                const tooltipText = target.getAttribute('data-tooltip');
                if (!tooltipText) return;
                const existingTooltip = document.querySelector('.custom-tooltip-sidebar');
                if (existingTooltip) existingTooltip.remove();
                const existingArrow = document.querySelector('.custom-tooltip-arrow-sidebar');
                if (existingArrow) existingArrow.remove();
                const rect = target.getBoundingClientRect();
                const tooltipLeft = rect.right + 10;
                const tooltipTop = rect.top + (rect.height / 2);
                const tooltip = document.createElement('div');
                tooltip.className = 'custom-tooltip-sidebar';
                tooltip.textContent = tooltipText;
                tooltip.style.cssText = `
                    position: fixed !important;
                    left: ${tooltipLeft}px !important;
                    top: ${tooltipTop}px !important;
                    transform: translateY(-50%) !important;
                    background: #1a1a2e !important;
                    color: #fff !important;
                    padding: 8px 14px !important;
                    border-radius: 10px !important;
                    font-size: 0.82rem !important;
                    font-weight: 500 !important;
                    white-space: nowrap !important;
                    z-index: 9999999 !important;
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4) !important;
                    pointer-events: none !important;
                    font-family: 'Poppins', sans-serif !important;
                    backdrop-filter: blur(8px) !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    max-width: 280px !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                `;
                const arrow = document.createElement('div');
                arrow.className = 'custom-tooltip-arrow-sidebar';
                arrow.style.cssText = `
                    position: fixed !important;
                    left: ${rect.right + 2}px !important;
                    top: ${tooltipTop}px !important;
                    transform: translateY(-50%) !important;
                    width: 0 !important;
                    height: 0 !important;
                    border-width: 6px !important;
                    border-style: solid !important;
                    border-color: transparent #1a1a2e transparent transparent !important;
                    z-index: 9999999 !important;
                    pointer-events: none !important;
                `;
                document.body.appendChild(arrow);
                document.body.appendChild(tooltip);
                target._tooltip = tooltip;
                target._arrow = arrow;
            };
            const hideTooltip = (e) => {
                const target = e.currentTarget;
                if (target._tooltip) {
                    target._tooltip.remove();
                    target._tooltip = null;
                }
                if (target._arrow) {
                    target._arrow.remove();
                    target._arrow = null;
                }
            };
            const tooltipElements = document.querySelectorAll('.dashboard.sidebar-collapsed [data-tooltip]');
            tooltipElements.forEach(item => {
                item.removeEventListener('mouseenter', showTooltip);
                item.removeEventListener('mouseleave', hideTooltip);
                item.addEventListener('mouseenter', showTooltip);
                item.addEventListener('mouseleave', hideTooltip);
            });
            return () => {
                tooltipElements.forEach(item => {
                    item.removeEventListener('mouseenter', showTooltip);
                    item.removeEventListener('mouseleave', hideTooltip);
                    if (item._tooltip) {
                        item._tooltip.remove();
                        item._tooltip = null;
                    }
                    if (item._arrow) {
                        item._arrow.remove();
                        item._arrow = null;
                    }
                });
            };
        }
    }, [sidebarCollapsed, activeLink, expandedMenu, reminders]);

    // ==============================================
    // 🔥 FORCE LOGOUT
    // ==============================================
    const forceLogout = useCallback(async (motivo) => {
        if (isForceLoggingOut.current) return;
        isForceLoggingOut.current = true;
        console.log(`🚪 Forçando logout: ${motivo}`);

        if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (serverCheckInterval.current) {
            clearInterval(serverCheckInterval.current);
            serverCheckInterval.current = null;
        }
        if (recoveryCheckRef.current) {
            clearInterval(recoveryCheckRef.current);
            recoveryCheckRef.current = null;
        }

        countdownStartedRef.current = false;
        wasOfflineRef.current = false;

        try {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    await fetch('/api/auth/logout', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                } catch (e) {
                    console.log('Não foi possível registrar logout no backend (servidor offline)');
                }
            }
        } catch (error) {
            console.error('Erro ao forçar logout:', error);
        } finally {
            localStorage.removeItem('dashboardActiveLink');
            localStorage.clear();
            sessionStorage.clear();
            document.cookie.split(";").forEach(function (c) {
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
            });
            window.location.href = '/';
        }
    }, []);

    // ==============================================
    // HELPERS DE ERRO
    // ==============================================
    const getStatusText = (statusCode) => {
        const statusMap = {
            400: 'Bad Request',
            401: 'Não Autorizado',
            403: 'Acesso Negado',
            404: 'Não Encontrado',
            500: 'Erro Interno do Servidor',
            502: 'Bad Gateway',
            503: 'Serviço Indisponível',
            504: 'Gateway Timeout'
        };
        return statusMap[statusCode] || 'Erro Desconhecido';
    };

    const getErrorDetailForStatus = (statusCode) => {
        const detailMap = {
            400: 'A requisição enviada contém dados inválidos. Verifique os parâmetros.',
            401: 'Sua sessão expirou ou o token de autenticação é inválido. Faça login novamente.',
            403: 'Você não tem permissão para acessar este recurso. Contacte o administrador.',
            404: 'O recurso solicitado não foi encontrado no servidor. Verifique a URL.',
            500: 'Ocorreu um erro interno no servidor. O administrador foi notificado.',
            502: 'O servidor intermediário recebeu uma resposta inválida do servidor de origem.',
            503: 'O servidor está temporariamente indisponível (sobrecarga ou manutenção).',
            504: 'O tempo limite de resposta do servidor foi excedido.'
        };
        return detailMap[statusCode] || 'Ocorreu um erro inesperado. Tente novamente mais tarde.';
    };

    const getErrorIcon = (code) => {
        const icons = {
            'TIMEOUT': '⏱️',
            'NETWORK_ERROR': '🌐',
            'CORS_ERROR': '🔒',
            'DNS_ERROR': '📍',
            'CONNECTION_REFUSED': '🚫',
            'HTTP_404': '📄',
            'HTTP_500': '⚙️',
            'HTTP_401': '🔑',
            'HTTP_403': '⛔',
            'HTTP_400': '❌',
            'HTTP_502': '🔌',
            'HTTP_503': '🔧',
            'HTTP_504': '⏳'
        };
        return icons[code] || '⚠️';
    };

    const getErrorTitle = (code) => {
        const titles = {
            'TIMEOUT': 'Tempo Limite Excedido',
            'NETWORK_ERROR': 'Erro de Rede',
            'CORS_ERROR': 'Erro de Segurança',
            'DNS_ERROR': 'Servidor Não Encontrado',
            'CONNECTION_REFUSED': 'Conexão Recusada',
            'HTTP_404': 'Recurso Não Encontrado',
            'HTTP_500': 'Erro Interno do Servidor',
            'HTTP_401': 'Sessão Expirada',
            'HTTP_403': 'Acesso Negado',
            'HTTP_400': 'Requisição Inválida',
            'HTTP_502': 'Gateway Inválido',
            'HTTP_503': 'Serviço Indisponível',
            'HTTP_504': 'Tempo Limite do Gateway'
        };
        return titles[code] || 'Erro de Conexão';
    };

    const getErrorLevel = (code) => {
        const levels = {
            'TIMEOUT': 'warning',
            'NETWORK_ERROR': 'critical',
            'CORS_ERROR': 'critical',
            'DNS_ERROR': 'warning',
            'CONNECTION_REFUSED': 'critical',
            'HTTP_404': 'warning',
            'HTTP_500': 'critical',
            'HTTP_401': 'warning',
            'HTTP_403': 'critical',
            'HTTP_400': 'warning',
            'HTTP_502': 'critical',
            'HTTP_503': 'warning',
            'HTTP_504': 'warning'
        };
        return levels[code] || 'critical';
    };

    const getPossibleCauses = (code) => {
        const causes = {
            'TIMEOUT': ['Servidor está sobrecarregado', 'Conexão de rede lenta', 'Firewall bloqueando a requisição', 'Servidor em manutenção'],
            'NETWORK_ERROR': ['Servidor não está em execução', 'Conexão com a internet interrompida', 'Firewall bloqueando a conexão', 'Endereço do servidor incorreto'],
            'CORS_ERROR': ['Configurações CORS incorretas no servidor', 'Domínio não autorizado', 'Política de segurança do navegador'],
            'DNS_ERROR': ['Endereço do servidor inválido', 'Servidor DNS indisponível', 'Arquivo hosts bloqueando o domínio'],
            'CONNECTION_REFUSED': ['Servidor não está em execução na porta especificada', 'Firewall bloqueando a porta', 'Servidor atingiu o limite máximo de conexões'],
            'HTTP_404': ['Endpoint da API não existe', 'URL incorreta', 'Recurso foi movido ou removido'],
            'HTTP_500': ['Erro interno no servidor', 'Problema com o banco de dados', 'Erro de lógica na aplicação'],
            'HTTP_401': ['Token de autenticação expirado', 'Credenciais inválidas', 'Sessão foi encerrada'],
            'HTTP_403': ['Permissões insuficientes', 'Acesso negado pelo administrador', 'Função sem autorização para este recurso'],
            'HTTP_400': ['Dados da requisição inválidos', 'Parâmetros obrigatórios faltando', 'Formato de dados incorreto'],
            'HTTP_502': ['Servidor intermediário com problemas', 'Timeout na comunicação entre servidores', 'Servidor de origem indisponível'],
            'HTTP_503': ['Servidor em manutenção programada', 'Servidor sobrecarregado com requisições', 'Servidor foi desligado temporariamente'],
            'HTTP_504': ['Tempo limite de resposta excedido', 'Servidor de origem muito lento', 'Gateway sem resposta']
        };
        return causes[code] || ['Erro desconhecido no servidor', 'Problema de comunicação com o servidor', 'Verifique sua conexão com a internet'];
    };

    const getErrorCodeDescription = (code) => {
        const descriptions = {
            'TIMEOUT': '⏱️ Tempo Limite Excedido',
            'NETWORK_ERROR': '🌐 Erro de Rede',
            'CORS_ERROR': '🔒 Erro de Segurança CORS',
            'DNS_ERROR': '📍 Servidor Não Encontrado',
            'CONNECTION_REFUSED': '🚫 Conexão Recusada',
            'HTTP_404': '📄 Recurso Não Encontrado',
            'HTTP_500': '⚙️ Erro Interno do Servidor',
            'HTTP_401': '🔑 Não Autorizado',
            'HTTP_403': '⛔ Acesso Negado',
            'HTTP_400': '❌ Requisição Inválida',
            'HTTP_502': '🔌 Gateway Inválido',
            'HTTP_503': '🔧 Serviço Indisponível',
            'HTTP_504': '⏳ Tempo Limite do Gateway'
        };
        return descriptions[code] || '❌ Erro Desconhecido';
    };

    // ==============================================
    // 🔥 RECUPERAÇÃO: SERVIDOR VOLTOU A FICAR ONLINE
    // ==============================================
    const handleRecuperacao = useCallback(() => {
        console.log('🔄 [RECUPERAÇÃO] Servidor online — a restaurar sessão...');

        countdownStartedRef.current = false;
        wasOfflineRef.current = false;

        if (recoveryCheckRef.current) {
            clearInterval(recoveryCheckRef.current);
            recoveryCheckRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
        }

        setShowServerError(false);
        setServerStatus('online');
        setServerErrorCode('');
        setServerErrorMessage('');
        setServerErrorDetail('');
        setServerErrorTimestamp(null);
        setServerErrorCount(0);
        setLogoutCountdown(30);

        if (activeLink) {
            localStorage.setItem('dashboardActiveLink', activeLink);
        }

        setTimeout(() => {
            window.location.reload();
        }, 300);
    }, [activeLink]);

    // ==============================================
    // 🔥 APLICAR ESTADO DE ERRO
    // ==============================================
    const aplicarEstadoErro = useCallback((errorCode, errorMessage, errorDetail, errorEntry = null) => {
        if (countdownStartedRef.current) {
            console.log('⏱️ Countdown já em andamento — atualizando apenas info do erro');
            setServerErrorCode(errorCode);
            setServerErrorMessage(errorMessage);
            setServerErrorDetail(errorDetail);
            if (errorEntry) {
                setServerErrorHistory(prev => [...prev, errorEntry].slice(-10));
            }
            return;
        }

        console.log('🔴 Novo erro detetado — iniciando countdown de 30s');
        countdownStartedRef.current = true;
        wasOfflineRef.current = true;

        if (errorEntry) {
            setServerErrorHistory(prev => [...prev, errorEntry].slice(-10));
        }
        setServerErrorCount(prev => prev + 1);
        setServerStatus('offline');
        setServerErrorCode(errorCode);
        setServerErrorMessage(errorMessage);
        setServerErrorDetail(errorDetail);
        setServerErrorTimestamp(new Date().toISOString());
        setShowServerError(true);
        setLogoutCountdown(30);

        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
        }

        countdownIntervalRef.current = setInterval(() => {
            setLogoutCountdown(prev => {
                const newCount = prev - 1;
                if (newCount <= 0) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                    forceLogout(`Servidor offline - tempo limite de 30 segundos excedido`);
                    return 0;
                }
                return newCount;
            });
        }, 1000);

        if (recoveryCheckRef.current) {
            clearInterval(recoveryCheckRef.current);
        }
        recoveryCheckRef.current = setInterval(async () => {
            if (!navigator.onLine) return;

            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);

                const res = await fetch('/api/auth/check-server', {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}` },
                    signal: controller.signal
                });
                clearTimeout(timeoutId);

                if (res.ok && wasOfflineRef.current) {
                    console.log('✅ Servidor RECUPERADO! A fazer refresh...');
                    handleRecuperacao();
                }
            } catch (e) {
                // Silencioso — continua a tentar
            }
        }, 2000);
    }, [forceLogout, handleRecuperacao]);

    // ==============================================
    // 🔥 LIMPAR ESTADO DE ERRO
    // ==============================================
    const limparEstadoErro = useCallback((responseTime = null) => {
        if (serverStatus !== 'online' || countdownStartedRef.current) {
            setServerStatus('online');
            setShowServerError(false);
            setServerErrorCode('');
            setServerErrorMessage('');
            setServerErrorDetail('');
            setServerErrorTimestamp(null);
            setServerErrorCount(0);
            setLogoutCountdown(30);

            countdownStartedRef.current = false;
            wasOfflineRef.current = false;

            if (logoutTimeoutRef.current) {
                clearTimeout(logoutTimeoutRef.current);
                logoutTimeoutRef.current = null;
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
            if (recoveryCheckRef.current) {
                clearInterval(recoveryCheckRef.current);
                recoveryCheckRef.current = null;
            }
        }
    }, [serverStatus]);

    // ==============================================
    // 🔥 CHECK SERVER STATUS
    // ==============================================
    const checkServerStatus = useCallback(async () => {
        if (isForceLoggingOut.current) return false;

        if (!navigator.onLine) {
            aplicarEstadoErro(
                'NETWORK_ERROR',
                'Sem conexão à internet.',
                'Verifique o cabo de rede ou o Wi-Fi. O sistema tentará reconectar automaticamente.',
                {
                    code: 'NETWORK_ERROR',
                    message: 'Sem conexão à internet',
                    timestamp: new Date().toISOString()
                }
            );
            return false;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) return false;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);
            const startTime = Date.now();
            const response = await fetch('/api/auth/check-server', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal
            });
            const responseTime = Date.now() - startTime;
            clearTimeout(timeoutId);
            if (response.ok) {
                limparEstadoErro(responseTime);
                return true;
            } else {
                let errorDetail = 'Servidor não respondeu corretamente';
                let statusCode = response.status;
                let errorData = null;
                try {
                    errorData = await response.json();
                    errorDetail = errorData.erro || errorData.message || `HTTP ${statusCode}: ${response.statusText}`;
                } catch (e) {
                    errorDetail = `HTTP ${statusCode}: ${response.statusText}`;
                }
                const errorEntry = {
                    code: `HTTP_${statusCode}`,
                    message: errorDetail,
                    timestamp: new Date().toISOString(),
                    statusCode: statusCode
                };
                setServerErrorHistory(prev => [...prev, errorEntry].slice(-10));
                throw new Error(errorDetail);
            }
        } catch (error) {
            let errorCode = 'NETWORK_ERROR';
            let errorMessage = 'Não foi possível estabelecer conexão com o servidor.';
            let errorDetail = 'Verifique sua conexão com a internet e tente novamente.';

            if (error.name === 'AbortError') {
                errorCode = 'TIMEOUT';
                errorMessage = 'O servidor não respondeu dentro do tempo limite.';
                errorDetail = 'O servidor pode estar sobrecarregado ou inativo. Tente novamente mais tarde.';
            } else if (error.message.includes('Failed to fetch')) {
                errorCode = 'NETWORK_ERROR';
                errorMessage = 'Não foi possível conectar ao servidor.';
                errorDetail = 'Verifique se o servidor está em execução e se a rede está funcionando.';
            } else if (error.message.includes('CORS')) {
                errorCode = 'CORS_ERROR';
                errorMessage = 'Erro de segurança na comunicação com o servidor.';
                errorDetail = 'Contate o administrador do sistema para verificar as configurações CORS.';
            } else if (error.message.includes('ENOTFOUND') || error.message.includes('getaddrinfo')) {
                errorCode = 'DNS_ERROR';
                errorMessage = 'Servidor não encontrado.';
                errorDetail = 'Verifique se o endereço do servidor está correto e se o DNS está funcionando.';
            } else if (error.message.includes('ECONNREFUSED')) {
                errorCode = 'CONNECTION_REFUSED';
                errorMessage = 'Conexão recusada pelo servidor.';
                errorDetail = 'O servidor pode estar offline ou bloqueando conexões. Verifique o firewall.';
            } else if (error.message.includes('HTTP')) {
                const statusMatch = error.message.match(/HTTP (\d+)/);
                if (statusMatch) {
                    const statusCode = parseInt(statusMatch[1]);
                    errorCode = `HTTP_${statusCode}`;
                    errorMessage = `Erro HTTP ${statusCode}: ${getStatusText(statusCode)}`;
                    errorDetail = getErrorDetailForStatus(statusCode);
                }
            }

            const errorEntry = {
                code: errorCode,
                message: errorMessage,
                detail: errorDetail,
                timestamp: new Date().toISOString(),
                originalError: error.message
            };

            if (!countdownStartedRef.current) {
                aplicarEstadoErro(errorCode, errorMessage, errorDetail, errorEntry);
            } else {
                console.log('⏱️ Erro detetado mas countdown já em andamento — mantendo timer');
            }
            return false;
        }
    }, [aplicarEstadoErro, limparEstadoErro]);

    const startServerMonitoring = useCallback(() => {
        checkServerStatus();
        serverCheckInterval.current = setInterval(() => {
            if (!isForceLoggingOut.current) {
                checkServerStatus();
            }
        }, 10000);
    }, [checkServerStatus]);

    const stopServerMonitoring = () => {
        if (serverCheckInterval.current) {
            clearInterval(serverCheckInterval.current);
            serverCheckInterval.current = null;
        }
        if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (recoveryCheckRef.current) {
            clearInterval(recoveryCheckRef.current);
            recoveryCheckRef.current = null;
        }
    };

    // ==============================================
    // 🔥 DETEÇÃO INSTANTÂNEA ONLINE/OFFLINE DO BROWSER
    // ==============================================
    useEffect(() => {
        const handleOffline = () => {
            if (isForceLoggingOut.current) return;
            if (countdownStartedRef.current) {
                console.log('📵 Offline detetado — countdown já em andamento, mantendo');
                return;
            }
            aplicarEstadoErro(
                'NETWORK_ERROR',
                'Sem conexão à internet.',
                'Verifique o cabo de rede ou o Wi-Fi. O sistema tentará reconectar automaticamente.',
                {
                    code: 'NETWORK_ERROR',
                    message: 'Sem conexão à internet',
                    timestamp: new Date().toISOString()
                }
            );
        };

        const handleOnline = () => {
            console.log('🌐 Browser online — verificando servidor...');
            if (countdownStartedRef.current) {
                checkServerStatus();
            } else {
                isOfflineHandledRef.current = false;
                checkServerStatus();
            }
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [aplicarEstadoErro, checkServerStatus]);

    const toggleSidebarCollapse = () => {
        const newState = !sidebarCollapsed;
        setSidebarCollapsed(newState);
        localStorage.setItem('sidebarCollapsed', newState);
    };

    const toggleRightContentCollapse = () => {
        const newState = !rightContentCollapsed;
        setRightContentCollapsed(newState);
        localStorage.setItem('rightContentCollapsed', newState);
    };

    const goToLicenca = () => {
        setActiveLink('Licenca');
        setSidebarOpen(false);
    };

    // ==============================================
    // 🔥 NOTIFICAÇÕES PUSH
    // ==============================================
    const revokeNotificationPermission = async () => {
        try {
            if (navigator.permissions && navigator.permissions.revoke) {
                try {
                    const permissionStatus = await navigator.permissions.revoke({ name: 'notifications' });
                    if (permissionStatus.state === 'prompt') {
                        showTempToast('🔕 Permissão Revogada', 'As notificações foram desativadas.', 'success');
                        return true;
                    }
                } catch (revokeError) {
                    console.warn('⚠️ Falha ao revogar permissão via Permissions API:', revokeError);
                }
            }
            showTempToast(
                'ℹ️ Como desativar notificações',
                'Clique no ícone de cadeado na barra de endereço e bloqueie as notificações manualmente.',
                'warning'
            );
            return false;
        } catch (error) {
            console.error('❌ Erro ao revogar permissão:', error);
            showTempToast(
                '❌ Erro',
                'Não foi possível revogar a permissão. Desative manualmente nas configurações do navegador.',
                'danger'
            );
            return false;
        }
    };

    const requestPushNotificationPermission = async () => {
        if (!('Notification' in window)) {
            showTempToast('Info', 'Este navegador não suporta notificações push', 'warning');
            return false;
        }
        if (Notification.permission === 'granted') {
            pushNotificationPermissionRef.current = true;
            setPushNotificationsEnabled(true);
            localStorage.setItem('pushNotificationsEnabled', 'true');
            showTempToast('Info', 'Notificações push ativadas!', 'success');
            return true;
        }
        if (Notification.permission === 'denied') {
            showTempToast('Atenção', 'Permissão de notificações negada. Ative nas configurações do navegador.', 'warning');
            pushNotificationPermissionRef.current = false;
            setPushNotificationsEnabled(false);
            localStorage.setItem('pushNotificationsEnabled', 'false');
            return false;
        }
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                pushNotificationPermissionRef.current = true;
                setPushNotificationsEnabled(true);
                localStorage.setItem('pushNotificationsEnabled', 'true');
                try {
                    const welcomeNotification = new Notification('🎉 Notificações Ativadas!', {
                        body: 'A partir de agora, você receberá alertas dos seus lembretes.',
                        icon: '/favicon.ico',
                        tag: 'welcome-notification',
                        requireInteraction: true,
                        silent: false
                    });
                    setTimeout(() => welcomeNotification.close(), 5000);
                } catch (e) {
                    console.log('Erro ao enviar notificação de boas-vindas:', e);
                }
                showTempToast('Sucesso', 'Notificações push ativadas!', 'success');
                return true;
            } else {
                pushNotificationPermissionRef.current = false;
                setPushNotificationsEnabled(false);
                localStorage.setItem('pushNotificationsEnabled', 'false');
                showTempToast('Atenção', 'Permissão de notificações negada.', 'warning');
                return false;
            }
        } catch (error) {
            console.error('❌ Erro ao solicitar permissão:', error);
            pushNotificationPermissionRef.current = false;
            setPushNotificationsEnabled(false);
            localStorage.setItem('pushNotificationsEnabled', 'false');
            return false;
        }
    };

    const openBrowserNotificationSettings = () => {
        if (navigator.userAgent.includes('Chrome') || navigator.userAgent.includes('Edg')) {
            window.open('chrome://settings/content/notifications', '_blank');
            window.open('Edge://settings/content/notifications', '_blank');
        } else if (navigator.userAgent.includes('Firefox')) {
            window.open('about:preferences#privacy', '_blank');
        } else if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
            showTempToast('ℹ️ Safari', 'Vá em Preferências > Websites > Notificações para gerenciar.', 'info');
        } else {
            showTempToast('ℹ️ Configurações', 'Acesse as configurações de notificações do seu navegador.', 'info');
        }
    };

    const syncPushNotificationState = () => {
        if (!('Notification' in window)) {
            setPushNotificationsEnabled(false);
            localStorage.setItem('pushNotificationsEnabled', 'false');
            pushNotificationPermissionRef.current = false;
            return;
        }
        const permission = Notification.permission;
        if (permission === 'denied') {
            setPushNotificationsEnabled(false);
            localStorage.setItem('pushNotificationsEnabled', 'false');
            pushNotificationPermissionRef.current = false;
            return;
        }
        if (permission === 'granted') {
            const saved = localStorage.getItem('pushNotificationsEnabled');
            const enabled = saved === null || saved === 'true';
            setPushNotificationsEnabled(enabled);
            pushNotificationPermissionRef.current = enabled;
            return;
        }
        if (permission === 'default') {
            const saved = localStorage.getItem('pushNotificationsEnabled');
            const enabled = saved === 'true' ? true : false;
            setPushNotificationsEnabled(enabled);
            pushNotificationPermissionRef.current = enabled;
        }
    };

    const showConfirmPopup = (icon, title, message, confirmText, confirmType = 'success') => {
        return new Promise((resolve) => {
            setConfirmPopup({
                icon,
                title,
                message,
                confirmText,
                confirmType,
                onConfirm: () => {
                    setConfirmPopup(null);
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmPopup(null);
                    resolve(false);
                }
            });
        });
    };

    const togglePushNotifications = async () => {
        if (!('Notification' in window)) {
            showTempToast('Info', 'Este navegador não suporta notificações push', 'warning');
            return;
        }
        const currentPermission = Notification.permission;
        if (!pushNotificationsEnabled && currentPermission === 'denied') {
            showTempToast(
                '🔕 Notificações Bloqueadas',
                'Ative as notificações nas configurações do navegador.',
                'warning'
            );
            return;
        }
        if (pushNotificationsEnabled) {
            const confirmed = await showConfirmPopup(
                '🔕',
                'Desativar Notificações',
                'Deseja realmente desativar as notificações push? Isso irá revogar a permissão do navegador.',
                'Sim, Desativar',
                'danger'
            );
            if (!confirmed) return;
            const revoked = await revokeNotificationPermission();
            setPushNotificationsEnabled(false);
            localStorage.setItem('pushNotificationsEnabled', 'false');
            pushNotificationPermissionRef.current = false;
            if (!revoked) {
                showTempToast(
                    '🔕 Desativado',
                    'As notificações foram desativadas no sistema. Para revogar a permissão, ajuste manualmente.',
                    'warning'
                );
                const openSettings = await showConfirmPopup(
                    '⚙️',
                    'Abrir Configurações',
                    'Deseja abrir as configurações de notificações do navegador para revogar a permissão manualmente?',
                    'Sim, Abrir',
                    'success'
                );
                if (openSettings) {
                    openBrowserNotificationSettings();
                }
            }
        } else {
            if (currentPermission === 'denied') {
                showTempToast(
                    '🔕 Notificações Bloqueadas',
                    'Ative as notificações nas configurações do navegador e recarregue a página.',
                    'warning'
                );
                const openSettings = await showConfirmPopup(
                    '⚙️',
                    'Abrir Configurações',
                    'Deseja abrir as configurações de notificações do navegador para ativar manualmente?',
                    'Sim, Abrir',
                    'success'
                );
                if (openSettings) {
                    openBrowserNotificationSettings();
                }
                return;
            }
            if (currentPermission === 'granted') {
                const confirmed = await showConfirmPopup(
                    '🔔',
                    'Ativar Notificações',
                    'As notificações já estão permitidas no navegador. Deseja ativar o envio de notificações do sistema?',
                    'Sim, Ativar',
                    'success'
                );
                if (!confirmed) return;
                setPushNotificationsEnabled(true);
                localStorage.setItem('pushNotificationsEnabled', 'true');
                pushNotificationPermissionRef.current = true;
                showTempToast('✅ Ativado', 'Notificações push ativadas com sucesso!', 'success');
                return;
            }
            const confirmed = await showConfirmPopup(
                '🔔',
                'Ativar Notificações',
                'Deseja ativar as notificações push? O navegador irá solicitar sua permissão.',
                'Sim, Ativar',
                'success'
            );
            if (!confirmed) return;
            const granted = await requestPushNotificationPermission();
            if (granted) {
                showTempToast('✅ Ativado', 'Notificações push ativadas com sucesso!', 'success');
            } else {
                showTempToast('❌ Falha', 'Não foi possível ativar as notificações.', 'danger');
            }
        }
    };

    const sendBrowserNotification = (title, body, icon = '/favicon.ico') => {
        if (!pushNotificationsEnabled) return;
        if (!('Notification' in window)) return;
        if (Notification.permission !== 'granted') {
            if (Notification.permission === 'denied') {
                syncPushNotificationState();
                return;
            }
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    pushNotificationPermissionRef.current = true;
                    setPushNotificationsEnabled(true);
                    localStorage.setItem('pushNotificationsEnabled', 'true');
                    showTempToast('✅ Sucesso', 'Notificações ativadas!', 'success');
                    sendBrowserNotification(title, body, icon);
                } else {
                    syncPushNotificationState();
                }
            });
            return;
        }
        try {
            const notification = new Notification(title, {
                body: body,
                icon: icon,
                tag: `lembrete-${Date.now()}`,
                requireInteraction: true,
                vibrate: [200, 100, 200, 100, 200],
                silent: false,
                timestamp: Date.now(),
                badge: '/favicon.ico'
            });
            setTimeout(() => {
                notification.close();
            }, 30000);
            notification.onclick = () => {
                notification.close();
                window.focus();
                playAlarmSound();
            };
        } catch (error) {
            console.error('❌ Erro ao enviar notificação push:', error);
        }
    };

    // ==============================================
    // 🔥 FUNÇÕES PARA LEMBRETES
    // ==============================================
    const playAlarmSound = () => {
        if (alarmPlayingRef.current) return;
        try {
            alarmPlayingRef.current = true;
            const audio1 = new Audio('/sons/alarm.mp3');
            audio1.volume = 0.9;
            audio1.play().catch(e => console.log('Audio play failed (1º toque):', e));
            setTimeout(() => {
                const audio2 = new Audio('/sons/alarm.mp3');
                audio2.volume = 0.9;
                audio2.play().catch(e => console.log('Audio play failed (2º toque):', e));
                setTimeout(() => {
                    alarmPlayingRef.current = false;
                }, 10000);
            }, 3000);
        } catch (e) {
            console.log('Audio play failed:', e);
            alarmPlayingRef.current = false;
        }
    };

    const showTempToast = (title, message, type = 'success', duration = 12) => {
        if (toastCountdownInterval.current) {
            clearInterval(toastCountdownInterval.current);
            toastCountdownInterval.current = null;
        }
        if (tempToast) {
            const toastElement = document.querySelector('.toast-notification.temp');
            if (toastElement) {
                toastElement.classList.add('removing');
            }
            setTimeout(() => {
                setTempToast(null);
                setToastCountdown(0);
            }, 400);
        }
        setToastCountdown(duration);
        setTempToast({ id: Date.now(), title, message, type, duration });
        toastCountdownInterval.current = setInterval(() => {
            setToastCountdown(prev => {
                const newCount = prev - 1;
                if (newCount <= 0) {
                    clearInterval(toastCountdownInterval.current);
                    toastCountdownInterval.current = null;
                    const toastElement = document.querySelector('.toast-notification.temp');
                    if (toastElement) {
                        toastElement.classList.add('removing');
                    }
                    setTimeout(() => {
                        setTempToast(null);
                        setToastCountdown(0);
                    }, 400);
                    return 0;
                }
                return newCount;
            });
        }, 1000);
    };

    const addPersistentNotification = (reminderId, title, message, reminderType, endDate, endTime) => {
        const isSpecific = reminderType === 'specific';
        setPersistentNotifications(prev => {
            const exists = prev.some(n => n.reminderId === reminderId);
            if (exists && isSpecific) return prev;
            if (!isSpecific && exists) return prev;
            const newNotification = {
                id: Date.now(),
                reminderId,
                title,
                message,
                timestamp: new Date().toISOString(),
                isNew: true,
                isSpecific: isSpecific,
                endDate: endDate || null,
                endTime: endTime || null,
                persistent: isSpecific
            };
            if (isSpecific) {
                try {
                    const saved = JSON.parse(localStorage.getItem('persistentReminderNotifications') || '[]');
                    const existsInStorage = saved.some(n => n.reminderId === reminderId);
                    if (!existsInStorage) {
                        saved.push({
                            reminderId,
                            title,
                            message,
                            timestamp: newNotification.timestamp,
                            endDate: endDate || null,
                            endTime: endTime || null
                        });
                        localStorage.setItem('persistentReminderNotifications', JSON.stringify(saved));
                    }
                } catch (e) {
                    console.error('Erro ao salvar notificação persistente:', e);
                }
            }
            setTimeout(() => {
                setPersistentNotifications(current =>
                    current.map(n =>
                        n.id === newNotification.id ? { ...n, isNew: false } : n
                    )
                );
            }, 600);
            const newList = [...prev, newNotification];
            if (newList.length > 5) {
                const nonPersistentIndex = newList.findIndex(n => !n.persistent);
                if (nonPersistentIndex !== -1) {
                    const oldestId = newList[nonPersistentIndex].id;
                    setRemovingNotificationId(oldestId);
                    setTimeout(() => {
                        setPersistentNotifications(current =>
                            current.filter(n => n.id !== oldestId)
                        );
                        setRemovingNotificationId(null);
                    }, 400);
                    return newList.filter((_, index) => index !== nonPersistentIndex);
                }
                const oldestId = newList[0].id;
                setRemovingNotificationId(oldestId);
                setTimeout(() => {
                    setPersistentNotifications(current =>
                        current.filter(n => n.id !== oldestId)
                    );
                    setRemovingNotificationId(null);
                }, 400);
                return newList.slice(1);
            }
            return newList;
        });
    };

    const loadPersistentNotifications = () => {
        try {
            const saved = JSON.parse(localStorage.getItem('persistentReminderNotifications') || '[]');
            if (saved.length > 0) {
                saved.forEach(notif => {
                    setPersistentNotifications(prev => {
                        const exists = prev.some(n => n.reminderId === notif.reminderId);
                        if (!exists) {
                            return [...prev, {
                                id: Date.now() + Math.random(),
                                reminderId: notif.reminderId,
                                title: notif.title,
                                message: notif.message,
                                timestamp: notif.timestamp || new Date().toISOString(),
                                isNew: false,
                                isSpecific: true,
                                persistent: true,
                                endDate: notif.endDate || null,
                                endTime: notif.endTime || null
                            }];
                        }
                        return prev;
                    });
                });
            }
        } catch (e) {
            console.error('Erro ao carregar notificações persistentes:', e);
        }
    };

    const removePersistentNotification = (notificationId) => {
        setRemovingNotificationId(notificationId);
        setTimeout(() => {
            setPersistentNotifications(prev => {
                const filtered = prev.filter(n => n.id !== notificationId);
                const removed = prev.find(n => n.id === notificationId);
                if (removed && removed.persistent) {
                    try {
                        const saved = JSON.parse(localStorage.getItem('persistentReminderNotifications') || '[]');
                        const updated = saved.filter(n => n.reminderId !== removed.reminderId);
                        localStorage.setItem('persistentReminderNotifications', JSON.stringify(updated));
                    } catch (e) {
                        console.error('Erro ao remover notificação persistente do localStorage:', e);
                    }
                }
                if (removed) {
                    triggeredRemindersRef.current.delete(removed.reminderId);
                }
                return filtered;
            });
            setRemovingNotificationId(null);
        }, 400);
    };

    const clearAllNotifications = () => {
        const ids = persistentNotifications.map(n => n.id);
        ids.forEach((id, index) => {
            setTimeout(() => {
                removePersistentNotification(id);
            }, index * 150);
        });
    };

    const fetchReminders = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get('/api/Lembrete', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReminders(response.data);
            setTimeout(() => {
                scheduleAllReminders(response.data);
            }, 1000);
        } catch (err) {
            console.error('Erro ao buscar lembretes:', err);
            showTempToast('Erro', 'Não foi possível carregar os lembretes', 'danger');
        }
    };

    const calculateNextTriggerTime = (reminder) => {
        const now = new Date();
        if (reminder.reminderType === 'interval') {
            const intervalMinutes = (reminder.intervalHours * 60) + reminder.intervalMinutes;
            if (intervalMinutes === 0) return null;
            const intervalMs = intervalMinutes * 60 * 1000;
            const lastTrigger = reminder.lastTriggered ? new Date(reminder.lastTriggered) : null;
            if (!lastTrigger) {
                if (reminder.createdAt) {
                    const createdTime = new Date(reminder.createdAt);
                    const nextTime = new Date(createdTime.getTime() + intervalMs);
                    if (nextTime <= now) {
                        const timeSinceCreated = now - createdTime;
                        const periodsPassed = Math.floor(timeSinceCreated / intervalMs) + 1;
                        return new Date(createdTime.getTime() + (periodsPassed * intervalMs));
                    }
                    return nextTime;
                }
                return new Date(now.getTime() + intervalMs);
            } else {
                const nextTime = new Date(lastTrigger.getTime() + intervalMs);
                if (nextTime <= now) {
                    const timeSinceLastTrigger = now - lastTrigger;
                    const periodsPassed = Math.floor(timeSinceLastTrigger / intervalMs) + 1;
                    return new Date(lastTrigger.getTime() + (periodsPassed * intervalMs));
                }
                return nextTime;
            }
        } else if (reminder.reminderType === 'specific') {
            let endDateTime = null;
            if (reminder.endDate && reminder.endTime) {
                endDateTime = new Date(`${reminder.endDate}T${reminder.endTime}`);
            } else if (reminder.endDate) {
                endDateTime = new Date(`${reminder.endDate}T23:59:59`);
            } else if (reminder.startDate) {
                const startDate = new Date(reminder.startDate);
                endDateTime = new Date(startDate);
                endDateTime.setDate(endDateTime.getDate() + 1);
                endDateTime.setHours(0, 0, 0, 0);
            }
            if (!endDateTime) return null;
            if (triggeredRemindersRef.current.has(reminder._id)) return null;
            if (now > endDateTime) return null;
            return endDateTime;
        }
        return null;
    };

    const scheduleReminder = (reminder) => {
        if (reminderTimeoutsRef.current.has(reminder._id)) {
            clearTimeout(reminderTimeoutsRef.current.get(reminder._id));
            reminderTimeoutsRef.current.delete(reminder._id);
        }
        if (reminder.reminderType === 'specific' && triggeredRemindersRef.current.has(reminder._id)) {
            return;
        }
        const nextTriggerTime = calculateNextTriggerTime(reminder);
        if (nextTriggerTime) {
            const now = new Date();
            const delay = nextTriggerTime.getTime() - now.getTime();
            if (delay > 0) {
                const timeoutId = setTimeout(() => {
                    triggerReminder(reminder);
                }, delay);
                reminderTimeoutsRef.current.set(reminder._id, timeoutId);
            } else if (delay <= 0 && delay > -10000) {
                setTimeout(() => triggerReminder(reminder), 1000);
            } else {
                const timeoutId = setTimeout(() => {
                    scheduleReminder(reminder);
                }, 10000);
                reminderTimeoutsRef.current.set(reminder._id, timeoutId);
            }
        }
    };

    const scheduleAllReminders = (remindersList) => {
        reminderTimeoutsRef.current.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        reminderTimeoutsRef.current.clear();
        remindersList.forEach(reminder => {
            scheduleReminder(reminder);
        });
    };

    const shouldReminderTrigger = (reminder, now) => {
        if (reminder.reminderType === 'interval') {
            const intervalMinutes = (reminder.intervalHours * 60) + reminder.intervalMinutes;
            if (intervalMinutes === 0) return false;
            const intervalMs = intervalMinutes * 60 * 1000;
            const lastTrigger = reminder.lastTriggered ? new Date(reminder.lastTriggered) : null;
            if (!lastTrigger) {
                if (reminder.createdAt) {
                    const createdTime = new Date(reminder.createdAt);
                    const timeSinceCreated = now - createdTime;
                    return timeSinceCreated >= intervalMs - 1000;
                }
                return false;
            }
            const timeSinceLastTrigger = now - lastTrigger;
            return timeSinceLastTrigger >= intervalMs - 1000;
        } else if (reminder.reminderType === 'specific') {
            let endDateTime = null;
            if (reminder.endDate && reminder.endTime) {
                endDateTime = new Date(`${reminder.endDate}T${reminder.endTime}`);
            } else if (reminder.endDate) {
                endDateTime = new Date(`${reminder.endDate}T23:59:59`);
            } else if (reminder.startDate) {
                const startDate = new Date(reminder.startDate);
                endDateTime = new Date(startDate);
                endDateTime.setDate(endDateTime.getDate() + 1);
                endDateTime.setHours(0, 0, 0, 0);
            }
            if (!endDateTime) return false;
            if (triggeredRemindersRef.current.has(reminder._id)) return false;
            const timeDiff = Math.abs(now.getTime() - endDateTime.getTime());
            return timeDiff <= 5000;
        }
        return false;
    };

    const triggerReminder = async (reminder) => {
        if (isProcessingRef.current) {
            setTimeout(() => triggerReminder(reminder), 1000);
            return;
        }
        isProcessingRef.current = true;
        try {
            const triggerKey = reminder._id;
            if (triggeredRemindersRef.current.has(triggerKey)) {
                isProcessingRef.current = false;
                return;
            }
            const now = new Date();
            if (!shouldReminderTrigger(reminder, now)) {
                scheduleReminder(reminder);
                isProcessingRef.current = false;
                return;
            }
            triggeredRemindersRef.current.add(triggerKey);
            playAlarmSound();
            const pushTitle = `⏰ ${reminder.title}`;
            const pushMessage = reminder.reminderType === 'interval'
                ? `🔄 Lembrete recorrente (${reminder.intervalHours}h ${reminder.intervalMinutes}min) - ${now.toLocaleTimeString()}`
                : `⚠️ ATENÇÃO: Prazo Final! - ${reminder.title}`;
            sendBrowserNotification(pushTitle, pushMessage);
            const persistentMessage = reminder.reminderType === 'interval'
                ? `🔄 Lembrete recorrente (${reminder.intervalHours}h ${reminder.intervalMinutes}min)`
                : `⚠️ ATENÇÃO: ${reminder.title} - Prazo Final!`;
            addPersistentNotification(
                reminder._id,
                reminder.title,
                persistentMessage,
                reminder.reminderType,
                reminder.endDate,
                reminder.endTime
            );
            showTempToast('⏰ ALARME', `${reminder.title} - ${reminder.reminderType === 'interval' ? 'Intervalo' : 'Prazo Final'}!`, 'warning', 12);
            await updateReminderTrigger(reminder._id);
            const updatedReminder = {
                ...reminder,
                lastTriggered: now.toISOString()
            };
            setReminders(prev => prev.map(r => r._id === reminder._id ? updatedReminder : r));
            if (reminder.reminderType === 'interval') {
                setTimeout(() => {
                    triggeredRemindersRef.current.delete(triggerKey);
                }, 1000);
                scheduleReminder(updatedReminder);
            }
        } catch (error) {
            console.error(`❌ Erro ao processar trigger do lembrete:`, error);
            setTimeout(() => {
                triggeredRemindersRef.current.delete(reminder._id);
                scheduleReminder(reminder);
            }, 5000);
        } finally {
            isProcessingRef.current = false;
        }
    };

    const validateReminder = (reminder) => {
        if (!reminder.title.trim()) {
            showTempToast('Erro', 'Por favor, insira um título para o lembrete', 'danger');
            return false;
        }
        if (reminder.reminderType === 'interval') {
            const totalMinutes = (reminder.intervalHours * 60) + reminder.intervalMinutes;
            if (totalMinutes === 0) {
                showTempToast('Erro', 'O intervalo deve ser maior que 0 minutos', 'danger');
                return false;
            }
            if (totalMinutes < 1) {
                showTempToast('Erro', 'O intervalo mínimo é 1 minuto', 'danger');
                return false;
            }
            return true;
        } else if (reminder.reminderType === 'specific') {
            if (!reminder.startDate) {
                showTempToast('Erro', 'Por favor, insira uma data de início', 'danger');
                return false;
            }
            if (!reminder.endDate) {
                showTempToast('Erro', 'Por favor, insira uma data de fim', 'danger');
                return false;
            }
            const startDateTime = new Date(reminder.startDate);
            const endDateTime = new Date(reminder.endDate);
            if (startDateTime > endDateTime) {
                showTempToast('Erro', 'A data de início deve ser anterior à data de fim', 'danger');
                return false;
            }
            if (reminder.endTime) {
                const startFull = new Date(`${reminder.startDate}T${reminder.startTime || '00:00'}`);
                const endFull = new Date(`${reminder.endDate}T${reminder.endTime}`);
                if (startFull >= endFull) {
                    showTempToast('Erro', 'A data/hora de início deve ser anterior à data/hora de fim', 'danger');
                    return false;
                }
            }
            return true;
        }
        return false;
    };

    const addReminder = async () => {
        if (!validateReminder(newReminder)) return;
        if (reminders.length >= 12) {
            showTempToast('Limite atingido', 'Você já tem 12 lembretes.', 'warning');
            return;
        }
        if (newReminder.reminderType === 'interval') {
            const existingIntervalReminder = reminders.some(r => r.reminderType === 'interval');
            if (existingIntervalReminder) {
                showTempToast('Limite atingido', 'Já existe um lembrete com intervalo regular. Apenas um é permitido.', 'warning');
                return;
            }
        }
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada. Faça login novamente.', 'danger');
                return;
            }
            const reminderData = {
                title: newReminder.title.trim(),
                icon: newReminder.icon,
                reminderType: newReminder.reminderType,
                intervalHours: newReminder.intervalHours,
                intervalMinutes: newReminder.intervalMinutes,
                startDate: newReminder.startDate,
                endDate: newReminder.endDate,
                startTime: newReminder.startTime,
                endTime: newReminder.endTime,
                createdAt: new Date().toISOString()
            };
            const response = await axios.post('/api/Lembrete', reminderData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const newReminderData = response.data;
            if (newReminderData.reminderType === 'interval') {
                newReminderData.lastTriggered = newReminderData.createdAt || new Date().toISOString();
            }
            setReminders(prev => [newReminderData, ...prev]);
            setTimeout(() => {
                scheduleReminder(newReminderData);
            }, 500);
            setShowReminderModal(false);
            setNewReminder({
                title: '',
                icon: 'alarm',
                reminderType: 'interval',
                intervalHours: 0,
                intervalMinutes: 1,
                startDate: '',
                endDate: '',
                startTime: '',
                endTime: '',
                lastTriggered: null
            });
            showTempToast('Lembrete adicionado', `"${newReminder.title}" foi adicionado com sucesso!`, 'success');
        } catch (err) {
            console.error('Erro ao adicionar lembrete:', err);
            showTempToast('Erro', err.response?.data?.erro || 'Erro ao adicionar lembrete', 'danger');
        }
    };

    const removeReminder = async (id) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            await axios.delete(`/api/Lembrete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (reminderTimeoutsRef.current.has(id)) {
                clearTimeout(reminderTimeoutsRef.current.get(id));
                reminderTimeoutsRef.current.delete(id);
            }
            setReminders(reminders.filter(r => r._id !== id));
            setPersistentNotifications(prev => {
                const removed = prev.filter(n => n.reminderId !== id);
                try {
                    const saved = JSON.parse(localStorage.getItem('persistentReminderNotifications') || '[]');
                    const updated = saved.filter(n => n.reminderId !== id);
                    localStorage.setItem('persistentReminderNotifications', JSON.stringify(updated));
                } catch (e) {
                    console.error('Erro ao remover do localStorage:', e);
                }
                return removed;
            });
            triggeredRemindersRef.current.delete(id);
            showTempToast('Lembrete removido', 'O lembrete foi removido com sucesso!', 'success');
        } catch (err) {
            console.error('Erro ao remover lembrete:', err);
            showTempToast('Erro', err.response?.data?.erro || 'Erro ao remover lembrete', 'danger');
        }
    };

    const updateReminderTrigger = async (reminderId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            await axios.put(`/api/Lembrete/${reminderId}/trigger`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            console.error('Erro ao atualizar trigger do lembrete:', err);
            throw err;
        }
    };

    const isReminderOverdue = (reminder) => {
        if (reminder.reminderType === 'specific') {
            let endDateTime = null;
            if (reminder.endDate && reminder.endTime) {
                endDateTime = new Date(`${reminder.endDate}T${reminder.endTime}`);
            } else if (reminder.endDate) {
                endDateTime = new Date(`${reminder.endDate}T23:59:59`);
            }
            if (endDateTime) {
                const now = new Date();
                return now > endDateTime;
            }
        }
        return false;
    };

    // ==============================================
    // CARREGAR LEMBRETES
    // ==============================================
    useEffect(() => {
        fetchReminders();
        loadPersistentNotifications();
        return () => {
            reminderTimeoutsRef.current.forEach((timeoutId) => {
                clearTimeout(timeoutId);
            });
            reminderTimeoutsRef.current.clear();
            if (toastCountdownInterval.current) {
                clearInterval(toastCountdownInterval.current);
                toastCountdownInterval.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (reminders.length > 0) {
            scheduleAllReminders(reminders);
            reminders.forEach(reminder => {
                if (reminder.reminderType === 'specific' && isReminderOverdue(reminder)) {
                    const hasNotification = persistentNotifications.some(n => n.reminderId === reminder._id);
                    if (!hasNotification && !triggeredRemindersRef.current.has(reminder._id)) {
                        triggerReminder(reminder);
                    }
                }
            });
        }
    }, [reminders]);

    // ==============================================
    // 🔥 SOLICITAR PERMISSÃO E SINCRONIZAR ESTADO
    // ==============================================
    useEffect(() => {
        if (user) {
            syncPushNotificationState();
            if (pushNotificationsEnabled) {
                if ('Notification' in window && Notification.permission === 'granted') {
                    pushNotificationPermissionRef.current = true;
                } else if ('Notification' in window && Notification.permission === 'denied') {
                    setPushNotificationsEnabled(false);
                    localStorage.setItem('pushNotificationsEnabled', 'false');
                    pushNotificationPermissionRef.current = false;
                } else if ('Notification' in window && Notification.permission === 'default') {
                    setTimeout(() => {
                        requestPushNotificationPermission();
                    }, 2000);
                }
            } else {
                if ('Notification' in window && Notification.permission === 'granted') {
                    setPushNotificationsEnabled(true);
                    localStorage.setItem('pushNotificationsEnabled', 'true');
                    pushNotificationPermissionRef.current = true;
                }
            }
        }
    }, [user]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncPushNotificationState();
            }
        };
        const handleFocus = () => {
            syncPushNotificationState();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    // ==============================================
    // 🔥 ESCUTAR EVENTO DE CORES ATUALIZADAS
    // ==============================================
    useEffect(() => {
        const handleCoresAtualizadas = (event) => {
            const { empresaId, corPrimaria, source } = event.detail || {};
            const empresaIdUser = user?.empresaId ? extrairEmpresaId(user.empresaId) : null;
            if (empresaIdUser && empresaId === empresaIdUser) {
                aplicarCorHeader(corPrimaria, darkMode);
            }
        };
        window.addEventListener('coresAtualizadas', handleCoresAtualizadas);
        return () => window.removeEventListener('coresAtualizadas', handleCoresAtualizadas);
    }, [user, darkMode]);

    // ==============================================
    // 🔥 EFECT PARA DARK MODE - REAPLICAR COR
    // ==============================================
    useEffect(() => {
        if (headerColorBase) {
            aplicarCorHeader(headerColorBase, darkMode);
        }
    }, [darkMode, headerColorBase]);

    // ==============================================
    // 🔥 useEffect para buscar dados do usuário E MÓDULOS
    // ==============================================
    useEffect(() => {
        const token = localStorage.getItem('token');
        const licencaSalva = JSON.parse(localStorage.getItem('licenca') || 'null');
        const usuarioSalvo = JSON.parse(localStorage.getItem('usuario') || 'null');
        if (!token) {
            setLoading(false);
            return;
        }
        if (licencaSalva) {
            if (!licencaSalva.ativa) {
                console.warn('⚠️ Licença salva está desativada');
            }
            if (licencaSalva.tipoPrazo !== 'Infinito' && licencaSalva.tipoPrazo !== 'Desativada' && licencaSalva.dataFim) {
                const dataFim = new Date(licencaSalva.dataFim);
                const hoje = new Date();
                if (dataFim < hoje) {
                    console.warn('⚠️ Licença salva está expirada');
                }
            }
        }
        let licencaInicial = licencaSalva || {};
        if (usuarioSalvo) {
            const licencaDoUsuario = extrairLicenca(usuarioSalvo);
            if (licencaDoUsuario && licencaDoUsuario.ativa) {
                licencaInicial = licencaDoUsuario;
            }
            const permissoes = usuarioSalvo.permissoesIndividuais || null;
            setPermissoesIndividuais(permissoes);
            setUser({ ...usuarioSalvo, licenca: licencaInicial });
        }
        axios.get('/api/users/me', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(async res => {
                if (res.data && res.data.success) {
                    let userData = res.data.data || res.data;
                    if (userData.usuario) {
                        userData = userData.usuario;
                    }
                    let licencaFinal = extrairLicenca(userData);
                    if (!licencaFinal.ativa && userData.empresaId) {
                        const empresaIdStr = extrairEmpresaId(userData.empresaId);
                        if (empresaIdStr) {
                            const licencaAPI = await buscarLicencaDaAPI(empresaIdStr);
                            if (licencaAPI && licencaAPI.ativa) {
                                licencaFinal = licencaAPI;
                            }
                        }
                    }
                    if (!licencaFinal.ativa && licencaSalva && licencaSalva.ativa) {
                        licencaFinal = licencaSalva;
                    }
                    if (licencaFinal && licencaFinal.ativa) {
                        localStorage.setItem('licenca', JSON.stringify(licencaFinal));
                    }
                    const permissoesIndividuaisData = userData.permissoesIndividuais || null;
                    setPermissoesIndividuais(permissoesIndividuaisData);
                    const userId = userData._id || userData.id;
                    const { nome, email, perfil, imagemPerfil, empresaId, modulosAtivos, ...resto } = userData;
                    const userCompleto = {
                        id: userId,
                        nome,
                        email,
                        perfil,
                        imagemPerfil,
                        empresaId: empresaId,
                        licenca: licencaFinal,
                        modulosAtivos: modulosAtivos || {},
                        permissoesIndividuais: permissoesIndividuaisData,
                        noticiasConfig: userData.noticiasConfig,
                        ...resto
                    };
                    setUser(userCompleto);

                    if (userCompleto.empresaId) {
                        const empresaIdStr = extrairEmpresaId(userCompleto.empresaId);
                        if (empresaIdStr) {
                            await carregarCorHeader(empresaIdStr);
                        } else {
                            aplicarCorHeader('#1a1a2e', darkMode);
                        }
                    } else {
                        aplicarCorHeader('#1a1a2e', darkMode);
                    }

                    if (empresaId) {
                        const empresaIdStr = extrairEmpresaId(empresaId);
                        if (empresaIdStr) {
                            await carregarModulosDaEmpresa(empresaIdStr, perfil);
                        } else {
                            setMenuFiltrado(getMenuEnriquecido());
                        }
                    } else {
                        setMenuFiltrado(getMenuEnriquecido());
                    }
                } else {
                    if (usuarioSalvo) {
                        const licencaDoUsuario = extrairLicenca(usuarioSalvo);
                        const permissoes = usuarioSalvo.permissoesIndividuais || null;
                        setPermissoesIndividuais(permissoes);
                        setUser({
                            ...usuarioSalvo,
                            licenca: licencaDoUsuario.ativa ? licencaDoUsuario : licencaSalva || {},
                            permissoesIndividuais: permissoes,
                            noticiasConfig: usuarioSalvo.noticiasConfig
                        });
                        if (usuarioSalvo.empresaId) {
                            const empresaIdStr = extrairEmpresaId(usuarioSalvo.empresaId);
                            if (empresaIdStr) {
                                await carregarCorHeader(empresaIdStr);
                            } else {
                                aplicarCorHeader('#1a1a2e', darkMode);
                            }
                        } else {
                            aplicarCorHeader('#1a1a2e', darkMode);
                        }
                        if (usuarioSalvo.empresaId) {
                            const empresaIdStr = extrairEmpresaId(usuarioSalvo.empresaId);
                            if (empresaIdStr) {
                                await carregarModulosDaEmpresa(empresaIdStr, usuarioSalvo.perfil);
                            } else {
                                setMenuFiltrado(getMenuEnriquecido());
                            }
                        } else {
                            setMenuFiltrado(getMenuEnriquecido());
                        }
                    }
                }
                setLoading(false);
            })
            .catch(async err => {
                console.error('❌ Erro ao buscar usuário:', err.response?.data || err.message);
                if (usuarioSalvo) {
                    const licencaDoUsuario = extrairLicenca(usuarioSalvo);
                    const permissoes = usuarioSalvo.permissoesIndividuais || null;
                    setPermissoesIndividuais(permissoes);
                    setUser({
                        ...usuarioSalvo,
                        licenca: licencaDoUsuario.ativa ? licencaDoUsuario : licencaSalva || {},
                        permissoesIndividuais: permissoes,
                        noticiasConfig: usuarioSalvo.noticiasConfig
                    });
                    if (usuarioSalvo.empresaId) {
                        const empresaIdStr = extrairEmpresaId(usuarioSalvo.empresaId);
                        if (empresaIdStr) {
                            await carregarCorHeader(empresaIdStr);
                        } else {
                            aplicarCorHeader('#1a1a2e', darkMode);
                        }
                    } else {
                        aplicarCorHeader('#1a1a2e', darkMode);
                    }
                    if (usuarioSalvo.empresaId) {
                        const empresaIdStr = extrairEmpresaId(usuarioSalvo.empresaId);
                        if (empresaIdStr) {
                            await carregarModulosDaEmpresa(empresaIdStr, usuarioSalvo.perfil);
                        } else {
                            setMenuFiltrado(getMenuEnriquecido());
                        }
                    } else {
                        setMenuFiltrado(getMenuEnriquecido());
                    }
                    setLoading(false);
                } else {
                    setUser(null);
                    setLoading(false);
                    if (err.response?.status === 401) {
                        forceLogout('Sessão expirada');
                    }
                }
            });
        startServerMonitoring();
        return () => {
            stopServerMonitoring();
            if (recoveryCheckRef.current) {
                clearInterval(recoveryCheckRef.current);
                recoveryCheckRef.current = null;
            }
        };
    }, []);

    // ==============================================
    // RECARREGAR MENU QUANDO MÓDULOS, USER OU PERMISSÕES MUDAREM
    // ==============================================
    useEffect(() => {
        if (user && modulosAtivos && Object.keys(modulosAtivos).length > 0) {
            const perfil = user.perfil || 'Normal';
            const empresaNome = empresaInfo?.nome || user.empresaNome || 'Empresa';
            const filtrado = filtrarMenu(modulosAtivos, perfil, empresaNome, permissoesIndividuais);
            setMenuFiltrado(filtrado);
        }
    }, [modulosAtivos, user, permissoesIndividuais]);

    // ==============================================
    // 🔥 FORÇAR ATUALIZAÇÃO DO MENU QUANDO NOTICIASCONFIG MUDAR
    // ==============================================
    useEffect(() => {
        if (user) {
            if (modulosAtivos && Object.keys(modulosAtivos).length > 0) {
                const perfil = user.perfil || 'Normal';
                const empresaNome = empresaInfo?.nome || user.empresaNome || 'Empresa';
                const filtrado = filtrarMenu(modulosAtivos, perfil, empresaNome, permissoesIndividuais);
                setMenuFiltrado(filtrado);
            }
        }
    }, [user?.noticiasConfig, user?.perfil, modulosAtivos]);

    // ==============================================
    // 🔥 ESCUTAR EVENTO DE ATUALIZAÇÃO DO USUÁRIO
    // ==============================================
    useEffect(() => {
        const handleUserUpdated = (event) => {
            const { userId, data } = event.detail || {};
            const currentUserId = user?.id || user?._id;

            if (userId && currentUserId && userId === currentUserId) {
                const token = localStorage.getItem('token');
                if (token) {
                    axios.get('/api/users/me', {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(res => {
                        if (res.data && res.data.success) {
                            let userData = res.data.data || res.data;
                            if (userData.usuario) {
                                userData = userData.usuario;
                            }
                            setUser(prev => ({ ...prev, ...userData }));
                        }
                    });
                }
            }
        };

        window.addEventListener('userUpdated', handleUserUpdated);
        return () => window.removeEventListener('userUpdated', handleUserUpdated);
    }, [user]);

    // ==============================================
    // 🔥 ESCUTAR EVENTO DE CONFIGURAÇÃO DE NOTÍCIAS ATUALIZADA
    // ==============================================
    useEffect(() => {
        const handleNoticiasConfigAtualizada = (event) => {
            const config = event.detail;

            if (config) {
                setUser(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        noticiasConfig: {
                            ativo: config.ativo !== undefined ? config.ativo : prev.noticiasConfig?.ativo,
                            fontes: config.fontes || prev.noticiasConfig?.fontes || []
                        }
                    };
                });

                if (modulosAtivos && Object.keys(modulosAtivos).length > 0) {
                    const perfil = user?.perfil || 'Normal';
                    const empresaNome = empresaInfo?.nome || user?.empresaNome || 'Empresa';
                    const filtrado = filtrarMenu(modulosAtivos, perfil, empresaNome, permissoesIndividuais);
                    setMenuFiltrado(filtrado);
                }
            }
        };

        window.addEventListener('noticiasConfigAtualizada', handleNoticiasConfigAtualizada);
        return () => window.removeEventListener('noticiasConfigAtualizada', handleNoticiasConfigAtualizada);
    }, [modulosAtivos, user, empresaInfo, permissoesIndividuais]);

    // Verificar conexão quando a janela ganhar foco
    useEffect(() => {
        const handleFocus = () => {
            if (serverStatus !== 'offline' && !isForceLoggingOut.current) {
                checkServerStatus();
            }
        };
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [serverStatus, checkServerStatus]);

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode-variables');
        } else {
            document.body.classList.remove('dark-mode-variables');
        }
    }, [darkMode]);

    // ==============================================
    // FUNÇÃO DE LOGOUT
    // ==============================================
    const openLogoutModal = () => {
        setShowLogoutModal(true);
    };

    const closeLogoutModal = () => {
        setShowLogoutModal(false);
    };

    const confirmLogout = async () => {
        setShowLogoutModal(false);
        setIsLoggingOut(true);
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const response = await axios.post('/api/auth/logout', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error('❌ Erro no logout:', error.response?.data?.erro || error.message);
        } finally {
            ['token', 'usuario', 'licenca', 'dashboardActiveLink'].forEach(k => localStorage.removeItem(k));
            window.location.href = '/';
        }
    };

    // ==============================================
    // INFORMAÇÕES DA LICENÇA
    // ==============================================
    const licencaInfo = user?.licenca || {};
    const licencaStatus = licencaInfo.tipoPrazo || 'Não definida';
    const licencaAtiva = licencaInfo.ativa === true;
    const licencaDataFim = licencaInfo.dataFim ? new Date(licencaInfo.dataFim).toLocaleDateString('pt-PT') : null;
    const licencaDataInicio = licencaInfo.dataInicio ? new Date(licencaInfo.dataInicio).toLocaleDateString('pt-PT') : null;
    const licencaChave = licencaInfo.chave || 'N/A';

    const getDisplayStatus = () => {
        if (!licencaAtiva) return 'DESATIVADA';
        if (licencaStatus === 'DataFim') return 'Especifico';
        return licencaStatus;
    };

    const getDiasRestantes = () => {
        if (licencaInfo.tipoPrazo === 'Infinito' || !licencaInfo.dataFim) return null;
        const dataFim = new Date(licencaInfo.dataFim);
        const hoje = new Date();
        const diffTime = dataFim - hoje;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const diasRestantes = getDiasRestantes();
    const isExpiringSoon = diasRestantes !== null && diasRestantes <= 30 && diasRestantes > 0;

    // ==============================================
    // 🔥 POPUP DE CONFIRMAÇÃO PERSONALIZADO
    // ==============================================
    const renderConfirmPopup = () => {
        if (!confirmPopup) return null;
        const { icon, title, message, confirmText, confirmType, onConfirm, onCancel } = confirmPopup;
        return (
            <div className="confirm-popup-overlay" onClick={onCancel}>
                <div className="confirm-popup-modal" onClick={(e) => e.stopPropagation()}>
                    <span className="confirm-popup-icon">{icon}</span>
                    <h3>{title}</h3>
                    <p>{message}</p>
                    <div className="confirm-popup-buttons">
                        <button className="confirm-popup-cancel" onClick={onCancel}>
                            Cancelar
                        </button>
                        <button className={`confirm-popup-confirm ${confirmType === 'danger' ? 'danger' : ''}`} onClick={onConfirm}>
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 POPUP DE ERRO DO SERVIDOR
    // ==============================================
    const renderServerErrorNotification = () => {
        if (!showServerError) return null;
        const errorLevel = getErrorLevel(serverErrorCode);
        const errorIcon = getErrorIcon(serverErrorCode);
        const errorTitle = getErrorTitle(serverErrorCode);
        const errorType = serverErrorCode.startsWith('HTTP_') ? 'http' : 'network';
        return (
            <div className="server-error-overlay">
                <div className="server-error-modal">
                    <div className="server-error-header">
                        <div className={`server-error-icon ${errorLevel}`}>
                            {errorIcon}
                        </div>
                        <div className="server-error-title-section">
                            <h3>{errorTitle}</h3>
                            <span className={`server-error-badge ${errorLevel}`}>
                                {errorLevel.toUpperCase()}
                            </span>
                        </div>
                    </div>
                    {serverErrorCode && (
                        <div className="server-error-code">
                            <span className="error-code-badge">
                                {getErrorCodeDescription(serverErrorCode)}
                            </span>
                            <span className="error-code-value">{serverErrorCode}</span>
                        </div>
                    )}
                    <div className="server-error-message">
                        <p>{serverErrorMessage}</p>
                    </div>
                    <div className="server-error-details">
                        <div className="server-error-detail-row">
                            <span className="detail-label"> Tipo:</span>
                            <span className={`detail-value ${errorType}`}>
                                {errorType === 'http' ? 'Erro HTTP' : 'Erro de Rede'}
                            </span>
                        </div>
                        <div className="server-error-detail-row">
                            <span className="detail-label"> Ocorrido em:</span>
                            <span className="detail-value">
                                {serverErrorTimestamp ? new Date(serverErrorTimestamp).toLocaleString('pt-PT') : 'Agora'}
                            </span>
                        </div>
                        {serverErrorCode && (
                            <div className="server-error-detail-row">
                                <span className="detail-label"> Código:</span>
                                <span className="detail-value code">{serverErrorCode}</span>
                            </div>
                        )}
                    </div>
                    <div className="server-error-causes">
                        <p className="causes-title">⚠️ Causas possíveis:</p>
                        <ul>
                            {getPossibleCauses(serverErrorCode).map((cause, index) => (
                                <li key={index}>{cause}</li>
                            ))}
                        </ul>
                    </div>
                    {serverErrorDetail && (
                        <div className="server-error-suggestion">
                            💡 <span>{serverErrorDetail}</span>
                        </div>
                    )}
                    <div className={`server-error-loading ${logoutCountdown <= 5 ? 'logout' : ''}`}>
                        <div className="loading-spinner-small"></div>
                        <p>
                            {logoutCountdown > 0 ? (
                                <> A encerrar sessão em <strong>{logoutCountdown}</strong> segundos...</>
                            ) : (
                                '🚨 A encerrar sessão...'
                            )}
                        </p>
                    </div>
                    {serverErrorHistory.length > 0 && (
                        <div className="server-error-history">
                            <details>
                                <summary>📋 Histórico de erros ({serverErrorHistory.length})</summary>
                                <div className="history-list">
                                    {serverErrorHistory.slice().reverse().map((error, index) => (
                                        <div key={index} className="history-item">
                                            <span className="history-time">
                                                {new Date(error.timestamp).toLocaleTimeString('pt-PT')}
                                            </span>
                                            <span className={`history-code ${error.code.startsWith('HTTP') ? 'http' : 'network'}`}>
                                                {error.code}
                                            </span>
                                            <span className="history-message">{error.message}</span>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderLogoutConfirmModal = () => {
        if (!showLogoutModal) return null;
        return (
            <div className="logout-modal-overlay" onClick={closeLogoutModal}>
                <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="logout-modal-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#ff3b30" />
                        </svg>
                    </div>
                    <h3>Deseja sair?</h3>
                    <p>Tem certeza que deseja encerrar a sua sessão?</p>
                    <div className="logout-modal-buttons">
                        <button className="logout-modal-cancel" onClick={closeLogoutModal}>
                            Cancelar
                        </button>
                        <button className="logout-modal-confirm" onClick={confirmLogout}>
                            Sair
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderSessionExpiredModal = () => {
        if (!showSessionExpiredModal) return null;
        return (
            <div className="session-expired-overlay" onClick={handleSessionExpired}>
                <div className="session-expired-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="session-expired-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 13c-2.33 0-4.67-1.67-6-4.5.07-1.5 2-2.5 6-2.5s5.93 1 6 2.5c-1.33 2.83-3.67 4.5-6 4.5z" fill="#ff9800" />
                        </svg>
                    </div>
                    <h3>Sessão Expirada</h3>
                    <p>A sua sessão expirou após 8 horas de atividade.</p>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-info-dark)', marginTop: '0.5rem' }}>
                        Por segurança, faça login novamente para continuar.
                    </p>
                    <div className="session-expired-buttons">
                        <button className="session-expired-login" onClick={handleRelogin}>
                            Fazer Login Novamente
                        </button>
                        <button className="session-expired-cancel" onClick={handleSessionExpired}>
                            Sair
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDERIZAÇÃO DO CONTEÚDO PRINCIPAL
    // ==============================================
    const renderMainContent = () => {
        switch (activeLink) {
            case 'Analitico':
                return <Analitico />;
            case 'Dashboard':
                return <DashMenu />;
            case 'Licenca':
                return renderLicencaContent();
            case 'Empresas':
                return <Empresas />;
            case 'Utilizadores':
                return <Utilizadores />;
            case 'Logs':
                return <Logs />;
            case 'Licencas':
                return <Licencas />;
            case 'Definicoes':
                return <Definicoes />;
            case 'Noticias':
                return <Noticias />;
            case 'Clientes':
                return <Clientes />;
            case 'Fornecedores':
                return <Fornecedores />;
            case 'Criar Produto':
                return <Produtos />;
            case 'Listar Produtos':
                return <ListarProdutos />;
            case 'Entrada de Stock':
                return <EntradaStock />;
            case 'Categorias':
                return <Categorias />;
            case 'Movimentos':
                return <Movimentos />;
            case 'Inventário':
                return <Inventario />;
            case 'Armazéns':
                return <Armazens />;
            case 'Cotações':
                return <Cotacoes />;
            case 'Serviços':
                return <Servicos />;
            case 'Faturas':
                return <Faturas />;
            case 'Recibos':
                return <Recibos />;
            case 'DashboardVendas':
                return <DashboardVendas />;
            case 'Financeiro':
                return <Financeiro />
            case 'GuiasEntrega':
                return <GuiasEntrega />;
            case 'Recebimentos':
                return <Recebimentos />;

            case 'RelatorioVendas':
                return <RelatorioVendas />;

            case 'Relatorios':
                return <Relatorios />;

            
            case 'Usuarios':
                return <Utilizadores />;
            default:
                return (
                    <div className="coming-soon">
                        <Icon name="dashboard" size={64} color="var(--color-primary)" />
                        <h2>{activeLink}</h2>
                        <p>Conteúdo em desenvolvimento...</p>
                    </div>
                );
        }
    };

    // ==============================================
    // 🔥 PÁGINA: Conteúdo da Licença
    // ==============================================
    const renderLicencaContent = () => {
        return (
            <div className="licenca-container">
                <div className="licenca-card">
                    <div className="licenca-header">
                        <Icon name="ticket" size={32} color="#4f46e5" />
                        <h2>Informação da Licença</h2>
                    </div>

                    <div className="licenca-details">
                        <div className="detail-row full-width">
                            <div className="detail-icon-chave">
                                <Icon name="vpn_key" size={38} />
                            </div>
                            <div className="detail-content-chaves-licenca">
                                <span className="detail-label">Chave da Licença</span>
                                <span className="detail-value">{licencaChave}</span>
                            </div>
                        </div>

                        <div className="detail-row status">
                            <div className="detail-icon">
                                <Icon name={licencaAtiva ? 'check_circle' : 'block'} size={18} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">Status</span>
                                <span className={`status-badge ${licencaAtiva ? 'active' : 'inactive'}`}>
                                    {licencaAtiva ? 'ATIVA' : 'INATIVA'}
                                </span>
                            </div>
                        </div>

                        <div className="detail-row prazo">
                            <div className="detail-icon">
                                <Icon name="schedule" size={18} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">Tipo de Prazo</span>
                                <span className="detail-value">{getDisplayStatus()}</span>
                            </div>
                        </div>

                        <div className="detail-row inicio">
                            <div className="detail-icon">
                                <Icon name="event" size={18} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">Data de Início</span>
                                <span className="detail-value">{licencaDataInicio || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="detail-row expiracao">
                            <div className="detail-icon">
                                <Icon name="event_busy" size={18} />
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">Data de Expiração</span>
                                <span className={`detail-value ${isExpiringSoon ? 'expiring-soon' : ''}`}>
                                    {licencaStatus === 'Infinito' ? 'Nunca expira' : (licencaDataFim || 'N/A')}
                                </span>
                            </div>
                        </div>

                        {diasRestantes !== null && licencaStatus !== 'Infinito' && (
                            <div className="detail-row dias">
                                <div className="detail-icon">
                                    <Icon name="hourglass_top" size={18} />
                                </div>
                                <div className="detail-content">
                                    <span className="detail-label">Dias Restantes</span>
                                    <span className={`detail-value ${diasRestantes <= 7 ? 'urgent' : (diasRestantes <= 30 ? 'warning' : '')}`}>
                                        {diasRestantes} {diasRestantes === 1 ? 'dia' : 'dias'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {isExpiringSoon && licencaAtiva && (
                        <div className="licenca-warning">
                            <Icon name="bell" size={18} color="#f59e0b" />
                            <span>
                                Atenção! A sua licença expira em <strong>{diasRestantes} dias</strong>. Contacte o suporte para renovação.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 NOTIFICAÇÕES EM ESCADA
    // ==============================================
    const renderPersistentNotifications = () => {
        if (persistentNotifications.length === 0) return null;
        return (
            <>
                <div
                    className="notification-counter"
                    onClick={clearAllNotifications}
                    title="Clique para limpar todas as notificações"
                >
                    {persistentNotifications.length}
                </div>
                <div className="persistent-notifications">
                    {persistentNotifications.map((notif, index) => {
                        const isRemoving = removingNotificationId === notif.id;
                        const isNew = notif.isNew;
                        return (
                            <div
                                key={notif.id}
                                className={`toast-notification persistent ${isRemoving ? 'removing' : ''} ${isNew ? 'new' : ''} ${notif.persistent ? 'persistent-reminder' : ''}`}
                                style={{
                                    zIndex: persistentNotifications.length - index,
                                    borderLeftColor: notif.persistent ? 'var(--color-danger)' : 'var(--color-primary)',
                                }}
                            >
                                <div className="toast-icon persistent" style={{ backgroundColor: notif.persistent ? 'var(--color-danger)' : 'var(--color-primary)' }}>
                                    <Icon name={notif.persistent ? 'warning' : 'bell'} size={18} color="white" />
                                </div>
                                <div className="toast-content">
                                    <div className="toast-title" style={{ color: notif.persistent ? 'var(--color-danger)' : 'var(--color-dark)' }}>
                                        {notif.persistent ? '⚠️ PRAZO FINAL!' : notif.title}
                                    </div>
                                    <div className="toast-message">{notif.message}</div>
                                    {notif.persistent && (
                                        <div className="toast-message" style={{ fontSize: '0.6rem', marginTop: '0.2rem', color: 'var(--color-danger)', fontWeight: 'bold' }}>
                                            ⏰ NOTIFICAÇÃO PERSISTENTE - Remova manualmente
                                        </div>
                                    )}
                                    <div className="toast-message" style={{ fontSize: '0.6rem', marginTop: '0.2rem', opacity: 0.6 }}>
                                        ⏰ {new Date(notif.timestamp).toLocaleTimeString()}
                                    </div>
                                </div>
                                <button
                                    className="toast-close"
                                    onClick={() => removePersistentNotification(notif.id)}
                                    title="Remover notificação"
                                >
                                    ×
                                </button>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderTempToast = () => {
        if (!tempToast) return null;
        return (
            <div className={`toast-notification temp ${tempToast.type}`}>
                <div className={`toast-icon ${tempToast.type}`}>
                    <Icon name={tempToast.type === 'success' ? 'check_circle' : (tempToast.type === 'warning' ? 'warning' : 'error')} size={20} />
                </div>
                <div className="toast-content">
                    <div className="toast-title">{tempToast.title}</div>
                    <div className="toast-message">{tempToast.message}</div>
                    <div className="toast-countdown" style={{
                        fontSize: '0.6rem',
                        color: 'var(--color-info-dark)',
                        marginTop: '0.2rem',
                        opacity: 0.7,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                    }}>
                        <span>⏱️</span>
                        <span>Fechando em {toastCountdown}s</span>
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => {
                        const toastElement = document.querySelector('.toast-notification.temp');
                        if (toastElement) {
                            toastElement.classList.add('removing');
                        }
                        if (toastCountdownInterval.current) {
                            clearInterval(toastCountdownInterval.current);
                            toastCountdownInterval.current = null;
                        }
                        setTimeout(() => {
                            setTempToast(null);
                            setToastCountdown(0);
                        }, 400);
                    }}
                >
                    ×
                </button>
            </div>
        );
    };

    // ==============================================
    // MODAL DE LEMBRETE
    // ==============================================
    const renderReminderModal = () => {
        if (!showReminderModal) return null;
        return (
            <div className="modal-overlay-reminder" onClick={() => setShowReminderModal(false)}>
                <div className="modal-container-reminder" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header-reminder">
                        <h3>Adicionar Lembrete</h3>
                        <button className="modal-close-reminder" onClick={() => setShowReminderModal(false)}>×</button>
                    </div>
                    <div className="modal-body-reminder">
                        <div className="form-group">
                            <label>Título do Lembrete</label>
                            <input type="text" placeholder="Ex: Reunião importante" value={newReminder.title} onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>Ícone</label>
                            <div className="icon-selector">
                                {reminderIcons.map(icon => (
                                    <div key={icon.name} className={`icon-option ${newReminder.icon === icon.icon ? 'selected' : ''}`} onClick={() => setNewReminder({ ...newReminder, icon: icon.icon })}>
                                        <Icon name={icon.icon} size={24} color="var(--color-primary)" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Tipo de Lembrete</label>
                            <select value={newReminder.reminderType} onChange={(e) => setNewReminder({ ...newReminder, reminderType: e.target.value })}>
                                <option value="interval">🔄 Intervalo Regular</option>
                                <option value="specific">📌 Período Específico</option>
                            </select>
                        </div>
                        {newReminder.reminderType === 'interval' ? (
                            <div className="form-group">
                                <label>Intervalo</label>
                                <div className="time-range">
                                    <div>
                                        <label style={{ fontSize: '0.7rem' }}>Horas</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={newReminder.intervalHours}
                                            onChange={(e) => {
                                                const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                                setNewReminder({ ...newReminder, intervalHours: isNaN(value) ? 0 : value });
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem' }}>Minutos</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="59"
                                            value={newReminder.intervalMinutes}
                                            onChange={(e) => {
                                                const value = e.target.value === '' ? 1 : parseInt(e.target.value, 10);
                                                setNewReminder({ ...newReminder, intervalMinutes: isNaN(value) ? 1 : Math.min(59, Math.max(1, value)) });
                                            }}
                                        />
                                    </div>
                                </div>
                                <small style={{ color: 'var(--color-info-dark)' }}>
                                    🔄 O lembrete tocará repetidamente a cada X horas e minutos
                                </small>
                                {reminders.some(r => r.reminderType === 'interval') && (
                                    <small style={{ color: 'var(--color-danger)', display: 'block', marginTop: '0.5rem' }}>
                                        ⚠️ Já existe um lembrete com intervalo regular. Apenas um é permitido.
                                    </small>
                                )}
                                {newReminder.intervalHours === 0 && newReminder.intervalMinutes < 1 && (
                                    <small style={{ color: 'var(--color-danger)', display: 'block', marginTop: '0.3rem' }}>
                                        ⚠️ O intervalo mínimo é 1 minuto
                                    </small>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Data de Início</label>
                                    <input type="date" value={newReminder.startDate} onChange={(e) => setNewReminder({ ...newReminder, startDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Hora de Início</label>
                                    <input type="time" value={newReminder.startTime} onChange={(e) => setNewReminder({ ...newReminder, startTime: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Data de Fim</label>
                                    <input type="date" value={newReminder.endDate} onChange={(e) => setNewReminder({ ...newReminder, endDate: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Hora de Fim</label>
                                    <input type="time" value={newReminder.endTime} onChange={(e) => setNewReminder({ ...newReminder, endTime: e.target.value })} />
                                </div>
                                <small style={{ color: 'var(--color-info-dark)' }}>
                                    📌 O lembrete tocará APENAS no momento do fim (data/hora fim).
                                    {!newReminder.endTime && ' Se não definir hora fim, tocará às 23:59:59 do dia de fim.'}
                                </small>
                            </>
                        )}
                    </div>
                    <div className="modal-footer-reminder">
                        <button className="btn-cancel-reminder" onClick={() => setShowReminderModal(false)}>Cancelar</button>
                        <button
                            className="btn-save-reminder"
                            onClick={addReminder}
                            disabled={newReminder.reminderType === 'interval' && newReminder.intervalHours === 0 && newReminder.intervalMinutes === 0}
                        >
                            Adicionar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ==============================================
    // 🔥 RENDERIZAÇÃO DO MENU LATERAL
    // ==============================================
    const renderSidebarMenu = () => {
        const menuParaRenderizar = menuFiltrado.length > 0 ? menuFiltrado : getMenuEnriquecido();

        const noticiasAtivo = noticiasEstaAtivo();

        const menuNoticias = noticiasAtivo ? {
            id: 'noticias',
            label: 'Notícias',
            icon: 'newspaper',
            link: 'Noticias',
            isSingle: true
        } : null;

        const dashboardItem = menuParaRenderizar.find(item => item.id === 'dashboard');
        const outrosItens = menuParaRenderizar.filter(item => item.id !== 'dashboard');

        let menuFinal = [];

        if (dashboardItem) {
            menuFinal.push(dashboardItem);
        }

        if (menuNoticias) {
            menuFinal.push(menuNoticias);
        }

        menuFinal = [...menuFinal, ...outrosItens];

        return menuFinal.map((menu) => {
            if (menu.id === 'noticias') {
                return (
                    <div
                        key={menu.id}
                        className={`menu-item ${activeLink === menu.link ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick(menu.link)}
                        data-tooltip={sidebarCollapsed ? menu.label : undefined}
                    >
                        <Icon name={menu.icon} size={20} color="currentColor" />
                        <h3>{menu.label}</h3>
                    </div>
                );
            }

            if (!canView(menu.allowedRoles, menu.modulo)) return null;
            if (!temAcessoModulo(menu.modulo)) return null;

            if (menu.isSingle) {
                return (
                    <div
                        key={menu.id}
                        className={`menu-item ${activeLink === menu.link ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick(menu.link)}
                        data-tooltip={sidebarCollapsed ? menu.label : undefined}
                    >
                        <Icon name={menu.icon} size={20} color="currentColor" />
                        <h3>{menu.label}</h3>
                    </div>
                );
            }

            const isExpanded = isMenuExpanded(menu.id);
            const hasVisibleChildren = menu.children && menu.children.some(child => {
                const canViewChild = canView(child.allowedRoles, child.modulo, child.submodulo);
                const hasAccess = temAcessoModulo(child.modulo, child.submodulo);
                return canViewChild && hasAccess;
            });

            if (!hasVisibleChildren) return null;

            return (
                <div key={menu.id} className="menu-group" data-modulo={menu.modulo || menu.id}>
                    <div
                        className={`menu-group-header ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleMenu(menu.id)}
                        data-tooltip={sidebarCollapsed ? menu.label : undefined}
                    >
                        <div className="menu-group-header-left">
                            <Icon name={menu.icon} size={20} color="currentColor" />
                            <h3>{menu.label}</h3>
                        </div>
                        <Icon
                            name={isExpanded ? "expand_less" : "expand_more"}
                            size={20}
                            className={`menu-group-arrow ${isExpanded ? 'rotated' : ''}`}
                        />
                    </div>
                    {isExpanded && (
                        <div className="menu-group-children">
                            {menu.children.map((child) => {
                                if (!canView(child.allowedRoles, child.modulo, child.submodulo)) return null;
                                if (!temAcessoModulo(child.modulo, child.submodulo)) return null;
                                return (
                                    <div
                                        key={child.link}
                                        className={`menu-item-child ${activeLink === child.link ? 'active' : ''}`}
                                        onClick={() => handleMenuItemClick(child.link)}
                                        data-tooltip={sidebarCollapsed ? child.label : undefined}
                                    >
                                        <Icon name={child.icon} size={18} color="currentColor" />
                                        <h4>{child.label}</h4>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        });
    };

    // ==============================================
    // RENDERIZAÇÃO PRINCIPAL
    // ==============================================
    if (loading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>

                <div className="loading-content">
                    <h2 className="loading-title">A carregar o sistema</h2>
                    <p className="loading-subtitle">Por favor, aguarde um momento...</p>
                    <div className="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>

                <p className="loading-version">RAF ERP v1.0.0</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="error-404-screen">
                <div className="error-404-code">404</div>

                <div className="error-404-title">Utilizador não encontrado</div>

                <p className="error-404-message">
                    A sua sessão expirou ou o utilizador não está mais disponível.
                </p>

                <p className="error-404-hint">
                    Faça login novamente para continuar utilizando o sistema.
                </p>

                <div className="error-404-verse">
                    <p className="verse-text">
                        "Não temas, porque eu sou contigo; não te assombres, porque eu sou o teu Deus;
                        eu te fortaleço, e te ajudo, e te sustento com a destra da minha justiça."
                    </p>
                    <p className="verse-author">— Isaías 41:10</p>
                </div>

                <button
                    className="error-404-button"
                    onClick={() => {
                        localStorage.removeItem('token');
                        localStorage.removeItem('usuario');
                        localStorage.removeItem('licenca');
                        localStorage.removeItem('dashboardActiveLink');
                        window.location.href = '/';
                    }}
                >
                    🔐 Ir para o Login
                </button>

                <p className="error-404-version">RAF ERP v1.0.0</p>
            </div>
        );
    }

    const userName = user.nome;
    const userRole = user.perfil;
    const userPhoto = user.imagemPerfil ? getImagemUrl(user.imagemPerfil) : null;

    const brilhoHeader = calcularBrilho(headerColor);
    const isCorEscuraBase = isCorEscuraPredefinida(headerColorBase);
    const usarTextoBranco = brilhoHeader < 128 || isCorEscuraBase || darkMode;

    const textoCor = usarTextoBranco ? '#ffffff' : '#1a1a2e';
    const textoCorSecundario = usarTextoBranco ? 'rgba(255,255,255,0.8)' : 'rgba(26,26,46,0.7)';

    return (
        <>
            <header className="header" style={{ backgroundColor: headerColor || '#1a1a2e' }}>
                <div className="logo">
                    <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
                        <Icon name="menu" size={24} color={textoCor} />
                    </button>
                    <button className="sidebar-toggle" onClick={toggleSidebarCollapse} title={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"}>
                        <Icon name={sidebarCollapsed ? "menu_open" : "menu"} size={20} color={textoCor} />
                    </button>
                    <h1 style={{ color: textoCor }}>RAF <span style={{ color: usarTextoBranco ? '#ffd800' : '#cc0050' }}>ERP</span></h1>
                </div>
                <div className="header-right">
                    <div className="dark-mode" onClick={() => {
                        setDarkMode(!darkMode);
                        setModoAutomatico(false);
                    }}>
                        <span className={!darkMode ? 'active' : ''}>
                            <Icon name="light_mode" size={14} color={!darkMode ? (usarTextoBranco ? '#1a1a2e' : textoCor) : 'var(--color-info-dark)'} />
                        </span>
                        <span className={darkMode ? 'active lua' : ''}>
                            <Icon name="dark_mode" size={14} color={darkMode ? (usarTextoBranco ? '#ffffff' : textoCor) : 'var(--color-info-dark)'} />
                        </span>
                    </div>
                    <div className="profile">
                        <div className="info">
                            <p style={{ color: textoCor }}>Olá, <b style={{ color: textoCor }}>{userName}</b></p>
                            <small style={{ color: textoCorSecundario }}>{userRole}</small>
                        </div>
                        <div className="profile-photo">
                            <Avatar name={userName} src={userPhoto} size={44} />
                        </div>
                    </div>
                </div>
            </header>

            <div className={`dashboard ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${rightContentCollapsed ? 'right-collapsed' : ''}`}>
                <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-menu">
                        {renderSidebarMenu()}
                    </div>
                    <div className="license-status" onClick={goToLicenca} style={{ cursor: 'pointer' }} data-tooltip={sidebarCollapsed ? `Licença: ${licencaAtiva ? getDisplayStatus() : 'DESATIVADA'}` : undefined}>
                        <div className="license-badge">
                            <Icon name="ticket" size={18} color="var(--color-primary)" />
                            <span className="license-text">Licença</span>
                        </div>
                        <span className={`license-status-badge ${licencaAtiva ? 'active' : 'inactive'}`}>
                            {licencaAtiva ? getDisplayStatus() : 'DESATIVADA'}
                        </span>
                    </div>
                    {licencaDataFim && licencaAtiva && licencaStatus !== 'Infinito' && (
                        <div className="license-expiry" onClick={goToLicenca} style={{ cursor: 'pointer' }}>
                            <small>Expira: {licencaDataFim}</small>
                            {diasRestantes !== null && diasRestantes <= 30 && (
                                <small style={{ color: '#ff9800', display: 'block' }}>({diasRestantes} dias restantes)</small>
                            )}
                        </div>
                    )}
                    {licencaStatus === 'Infinito' && licencaAtiva && (
                        <div className="license-expiry" onClick={goToLicenca} style={{ cursor: 'pointer' }}>
                            <small> Licença Vitalícia</small>
                        </div>
                    )}
                    <div className="divider"></div>
                    <div className="sidebar-footer">
                        <div className="menu-item" onClick={openLogoutModal} data-tooltip={sidebarCollapsed ? "Sair" : undefined}>
                            <Icon name="logout" size={20} color="currentColor" />
                            <h3>{isLoggingOut ? 'A sair...' : 'Sair'}</h3>
                        </div>
                    </div>
                </aside>
                <main className="main-content">
                    {renderMainContent()}
                </main>
                <div className="right-content">
                    <button className="right-content-toggle" onClick={toggleRightContentCollapse} title={rightContentCollapsed ? "Expandir lembretes" : "Colapsar lembretes"}>
                        <Icon name={rightContentCollapsed ? "chevron_left" : "chevron_right"} size={20} color="var(--color-dark)" />
                    </button>
                    <div className="reminders">
                        <div className="header">
                            <h2>Lembretes <span className="reminders-badge">{reminders.length}/12</span></h2>
                            <div className="reminder-header-actions">
                                <button
                                    className={`push-toggle-btn ${pushNotificationsEnabled ? 'active' : 'inactive'}`}
                                    onClick={togglePushNotifications}
                                    title={pushNotificationsEnabled ? 'Desativar notificações push' : 'Ativar notificações push'}
                                >
                                    <Icon
                                        name={pushNotificationsEnabled ? 'notifications_active' : 'notifications_off'}
                                        size={18}
                                        color={pushNotificationsEnabled ? '#4CAF50' : '#f44336'}
                                    />
                                </button>
                            </div>
                        </div>
                        {reminders.length === 0 ? (
                            <div className="notification" style={{ justifyContent: 'center', opacity: 0.6 }}>
                                <div className="content">
                                    <small>Nenhum lembrete ativo</small>
                                </div>
                            </div>
                        ) : (
                            reminders.map(reminder => {
                                const isOverdue = reminder.reminderType === 'specific' && isReminderOverdue(reminder);
                                const hasPersistentNotification = persistentNotifications.some(n => n.reminderId === reminder._id && n.persistent);
                                return (
                                    <div
                                        key={reminder._id}
                                        className={`notification ${isOverdue || hasPersistentNotification ? 'overdue' : ''}`}
                                        data-title={reminder.title}
                                        style={{
                                            borderLeftColor: isOverdue || hasPersistentNotification ? 'var(--color-danger)' : '',
                                            backgroundColor: isOverdue || hasPersistentNotification ? 'rgba(255, 0, 0, 0.05)' : '',
                                        }}
                                    >
                                        <div className={`icon ${isOverdue || hasPersistentNotification ? 'danger' : reminder.reminderType === 'interval' ? 'success' : 'warning'}`}>
                                            <Icon name={reminder.icon} size={18} color="#fff" />
                                        </div>
                                        <div className="content">
                                            <h3 style={{ color: isOverdue || hasPersistentNotification ? 'var(--color-danger)' : '' }}>
                                                {isOverdue || hasPersistentNotification ? '⚠️ ' : ''}{reminder.title}
                                            </h3>
                                            <small>
                                                {reminder.reminderType === 'interval' ? (
                                                    <>🔄 A cada {reminder.intervalHours}h {reminder.intervalMinutes}min</>
                                                ) : (
                                                    <>📌 Fim: {reminder.endDate || reminder.startDate} {reminder.endTime || '23:59'}</>
                                                )}
                                                {(isOverdue || hasPersistentNotification) && (
                                                    <span style={{ color: 'var(--color-danger)', fontWeight: 'bold', marginLeft: '0.5rem' }}>
                                                        ⚠️ PRAZO FINAL!
                                                    </span>
                                                )}
                                            </small>
                                        </div>
                                        <div style={{ cursor: 'pointer', padding: '0 0.5rem' }} onClick={() => removeReminder(reminder._id)}>
                                            <Icon name="close" size={16} color="var(--color-danger)" />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        {reminders.length >= 12 && (
                            <div className="limit-warning">
                                ⚠️ Limite máximo de 12 lembretes atingido
                            </div>
                        )}
                        <div className="add-reminder" onClick={() => setShowReminderModal(true)}>
                            <Icon name="add" size={18} color="var(--color-primary)" />
                            <span>Adicionar Lembrete</span>
                        </div>
                    </div>
                </div>
            </div>
            {renderPersistentNotifications()}
            {renderTempToast()}
            {renderReminderModal()}
            {renderConfirmPopup()}
            {renderServerErrorNotification()}
            {renderLogoutConfirmModal()}
            {renderSessionExpiredModal()}
        </>
    );
}